/**
 * Provides the logic for all complex dataset-related property editors, such as dataset selectors,
 * filter editors, and metric editors. These are registered with the main editor system.
 */
namespace distill.dashboards {
    const MIN_RESULTS_FOR_SEARCH: string = '10';

    // Dataset references only store the id - resolve the current display name from the designer's datasets.
    // Restricted datasets are masked server-side, so they resolve to "Restricted".
    function getDatasetRefName(datasetRef: IDatasetReference): string {
        return designer.getDatasets().find(t => t.id === datasetRef.id)?.name
            || designer.getRestrictedDatasets().find(t => t.id === datasetRef.id)?.name
            || '';
    }

    export abstract class DataQueryEditorBase extends EditorBase {
        protected init() {
            /* Nothing to do, let base handle it all */
        }

        protected getDataQuery(): IDataQuery {
            if (this.propDef.config && this.propDef.config.dataQuery)
                return (this.component as any)[this.propDef.config.dataQuery] as IDataQuery;
            
            const rootPropName = this.propDef.path?.split('.')[0] || this.propName;
            return (this.component as any)[rootPropName] as IDataQuery;
        }
    }

    export interface ILinkEditorContext {
        linkData: IDatasetLink;
        datasets: IDatasetMeta[];
        datasetRefs: IDatasetReference[];
        // Restricted datasets the user is still allowed to bind (those already in the session's query)
        restrictedDatasetIds: Set<string>;
        onInput: (newLinkData: IDatasetLink) => void;
        onChanged: (newLinkData: IDatasetLink, forceAliasCheck?: boolean) => void;
        onDelete: () => void;
    }

    class DatasetLinkEditor {
        private $datasetLinkRow: JQuery;
        private $linkedDataset: JQuery;
        private $linkedDatasetAlias: JQuery;
        private $linkedColumn: JQuery;
        private $primaryDataset: JQuery;
        private $primaryColumn: JQuery;
        private $linkedColumnContainer: JQuery;
        private $primaryColumnContainer: JQuery;
        private context: ILinkEditorContext | null = null;
        private isFirstLink: boolean = false;

        public constructor(container: JQuery) {
            this.$datasetLinkRow = appn.utils.cloneTemplate('editor-datasetSelectorLinkRow');
            container.append(this.$datasetLinkRow);

            this.$linkedDatasetAlias = this.$datasetLinkRow.find('.linkedDatasetAlias');
            this.$linkedDataset = this.$datasetLinkRow.find('.linkedDatasetSelect');
            this.$linkedColumn = this.$datasetLinkRow.find('.linkedColumnSelect');
            this.$primaryDataset = this.$datasetLinkRow.find('.primaryDatasetSelect');
            this.$primaryColumn = this.$datasetLinkRow.find('.primaryColumnSelect');
            this.$linkedColumnContainer = this.$datasetLinkRow.find('.linkedColumnContainer');
            this.$primaryColumnContainer = this.$datasetLinkRow.find('.primaryColumnContainer');

            this.bindEvents();
        }

        public update(context: ILinkEditorContext, isFirstLink: boolean = false): void {
            this.context = context;
            this.isFirstLink = isFirstLink;
            const datasets = designer.getDatasets();
            const linkData = this.context.linkData;
            const fromRef = this.isFirstLink
                ? this.context.datasetRefs[0]
                : (appn.utils.hasValue(linkData.primaryDatasetIndex) ? this.context.datasetRefs[linkData.primaryDatasetIndex!] : undefined);

            // A restricted dataset has no editable columns, so only its own column dropdown is hidden
            const fromRestricted = !!fromRef && designer.isRestricted(fromRef.id);
            const toRestricted = designer.isRestricted(linkData.linkedDataset.id);

            this.$primaryColumnContainer.toggle(!fromRestricted);
            this.$linkedColumnContainer.toggle(!toRestricted);

            this.$linkedDatasetAlias.val(linkData.linkedDataset.alias || '');

            // --- "From" Row: Existing dataset + column to join from ---
            // Populate "From Dataset" (Context datasets: Primary + preceding links)
            this.$primaryDataset.empty();
            if (this.isFirstLink) {
                // First link: only primary dataset available, show as disabled
                const primaryRef = this.context.datasetRefs[0];
                if (primaryRef) {
                    const name = getDatasetRefName(primaryRef);
                    const text = appn.utils.hasValue(primaryRef.alias) && name !== primaryRef.alias 
                        ? `${name} (${primaryRef.alias})` : name;
                    const $opt = $('<option>').val(0).text(text);
                    this._styleRestrictedOption($opt, primaryRef.id);
                    this.$primaryDataset.append($opt);
                    this.$primaryDataset.val('0');
                }
                this.$primaryDataset.prop('disabled', true);
            } else {
                // Subsequent links: show all available datasets (primary + preceding links)
                this.$primaryDataset.append(`<option value="">${T('Choose dataset')}...</option>`);
                this.context.datasetRefs.forEach((t, index) => {
                    const name = getDatasetRefName(t);
                    const text = appn.utils.hasValue(t.alias) && name !== t.alias ? `${name} (${t.alias})` : name;
                    const $opt = $('<option>').val(index).text(text);
                    this._styleRestrictedOption($opt, t.id);
                    this.$primaryDataset.append($opt);
                });
                const primaryIndexValue = appn.utils.hasValue(linkData.primaryDatasetIndex) ? linkData.primaryDatasetIndex!.toString() : '';
                this.$primaryDataset.val(primaryIndexValue);
                this.$primaryDataset.prop('disabled', false);
            }

            // Populate "From Column" (Columns from selected "From Dataset")
            this.$primaryColumn.empty().append(`<option value="">${T('Choose column')}...</option>`);
            if (fromRef) {
                const fromDataset = datasets.find(t => t.id === fromRef.id);
                if (fromDataset) {
                    fromDataset.columns.forEach(col => {
                        this.$primaryColumn.append($('<option>')
                            .val(col.id)
                            .text(getColumnOptionText(col)));
                    });
                    this.$primaryColumn.val(linkData.primaryColumnId || '');
                }
            }

            // --- "To" Row: New dataset to link in ---
            // Populate "To Dataset" (All datasets)
            this.$linkedDataset.empty().append(`<option value="">${T('Choose dataset')}...</option>`);
            datasets.forEach(t => {
                const $opt = $('<option>').val(t.id);
                buildDatasetOption($opt, t);
                this.$linkedDataset.append($opt);
            });
            // Keeps restricted dataset bound until user saves, plus any restricted dataset already in the session
            // so the user can re-bind one after switching the "To" dataset away and back
            const restrictedDatasetIds = this.context.restrictedDatasetIds;
            designer.getRestrictedDatasets()
                .filter(t => t.id === linkData.linkedDataset.id || restrictedDatasetIds.has(t.id))
                .forEach(t => {
                    const $opt = $('<option>').val(t.id).attr('data-restricted', 'true');
                    buildDatasetOption($opt, t, true);
                    this.$linkedDataset.append($opt);
                });
            this.$linkedDataset.val(linkData.linkedDataset.id || '');

            // Populate "To Column" (Columns from selected "To Dataset")
            this.$linkedColumn.empty().append(`<option value="">${T('Choose column')}...</option>`);
            const toDataset = datasets.find(t => t.id === linkData.linkedDataset.id);
            if (toDataset) {
                toDataset.columns.forEach(col => {
                    this.$linkedColumn.append($('<option>')
                        .val(col.id)
                        .text(getColumnOptionText(col)));
                });
                this.$linkedColumn.val(linkData.linkedColumnId || '');
            }

            // --- Enablement Logic ---
            const hasFromColumn = !!linkData.primaryColumnId;
            const hasToDataset = !!linkData.linkedDataset.id;

            // "From Column" is enabled when from dataset is available (always true for first link)
            this.$primaryColumn.prop('disabled', !fromRef);

            // "To Dataset" is enabled when "From Column" is selected. Restricted datasets are the exception:
            // a restricted "To" can be switched to a non-restricted one, and a restricted "From" can never
            // supply a column, so it must not block the "To" either
            this.$linkedDataset.prop('disabled', !toRestricted && !fromRestricted && !hasFromColumn);

            // "To Column" is enabled when "To Dataset" is selected
            this.$linkedColumn.prop('disabled', !hasToDataset);

            // Alias is enabled when "To Dataset" is selected - a restricted dataset still has a meaningful
            // relationship alias (the user is naming the join, not editing the dataset's config)
            this.$linkedDatasetAlias.prop('disabled', !hasToDataset);
        }

        // Styles a "From"/"To" dataset option for a restricted dataset (lock glyph + text-danger)
        private _styleRestrictedOption($opt: JQuery, datasetId: string): void {
            const restrictedMeta = designer.getRestrictedDatasets().find(d => d.id === datasetId);
            if (!restrictedMeta)
                return;

            $opt.attr('data-icon-class', 'fa fa-lock');
            $opt.attr('data-container-class', 'restricted-option text-danger');
        }

        private bindEvents(): void {
            this.$datasetLinkRow.on('click', '.removeLink', () => this.context?.onDelete());

            this.$datasetLinkRow.on('input', '.linkedDatasetAlias', e => {
                if (this.context) {
                    this.context.linkData.linkedDataset.alias = $(e.target).val() as string;
                    this.context.onInput(this.context.linkData);
                }
            });
            this.$datasetLinkRow.on('change', '.linkedDatasetAlias', e => {
                if (this.context) {
                    this.context.linkData.linkedDataset.alias = $(e.target).val() as string;
                    // Pass false for forceAliasCheck (standard blur)
                    this.context.onChanged(this.context.linkData, false);
                }
            });

            // --- "To" Dataset and Column (new dataset to link in) ---
            this.$datasetLinkRow.on('change', '.linkedDatasetSelect', e => {
                if (!this.context)
                    return;

                const datasetId = $(e.target).val() as string;
                const dataset = designer.getDatasets().find(t => t.id === datasetId)
                    || designer.getRestrictedDatasets().find(t => t.id === datasetId);

                let newLink = appn.utils.deepClone(this.context.linkData);
                
                if (dataset) {
                    newLink.linkedDataset.id = dataset.id;
                    // Auto-select first column (typically Id). Restricted datasets show no columns, we keep them bound.
                    if (dataset.columns.length > 0)
                        newLink.linkedColumnId = dataset.columns[0].id;
                    else if (this.context.linkData.linkedDataset.id !== dataset.id)
                        newLink.linkedColumnId = '';
                } else {
                    newLink.linkedDataset.id = '';
                    newLink.linkedColumnId = '';
                }
                
                newLink.linkedDataset.alias = '';
                this.context.onChanged(newLink, false);
            });

            this.$datasetLinkRow.on('change', '.linkedColumnSelect', e => {
                if (this.context) {
                    // This is the trigger for the self-link de-dupe check.
                    this.context.onChanged({ ...this.context.linkData, linkedColumnId: $(e.target).val() as string }, true);
                }
            });

            // --- "From" Dataset and Column (existing dataset to join from) ---
            this.$datasetLinkRow.on('change', '.primaryDatasetSelect', e => {
                if (!this.context || this.isFirstLink)
                    return;
                const indexStr = $(e.target).val() as string;
                const index = indexStr ? parseInt(indexStr, 10) : undefined;
                this.context.onChanged({ ...this.context.linkData, primaryDatasetIndex: index, primaryColumnId: '' }, false);
            });

            this.$datasetLinkRow.on('change', '.primaryColumnSelect', e => {
                if (this.context)
                    this.context.onChanged({ ...this.context.linkData, primaryColumnId: $(e.target).val() as string }, false);
            });
        }

        public destroy(): void {
            this.$datasetLinkRow.remove();
        }
    }
    class DatasetSelectorEditor extends DataQueryEditorBase {
        protected $datasetSelect!: JQuery;
        protected $aliasInput!: JQuery;
        protected $aliasContainer!: JQuery;
        protected $linksList!: JQuery;
        protected $addLink!: JQuery;
        private linkEditors: DatasetLinkEditor[] = [];
        private datasets: IDatasetMeta[] = [];
        private _restrictedDatasets: IDatasetMeta[] = [];
        private _loadedQueryDatasetIds: Set<string> = new Set();
        private _queryGuard: IPathGuard | null = null;

        init() {
            this.$datasetSelect = this.$editor.find('.primaryDsSelect');
            this.$aliasInput = this.$editor.find('.primaryDsAlias');
            this.$aliasContainer = this.$editor.find('.primaryDsAliasContainer');
            this.$linksList = this.$editor.find('.dataSourceLinksList');
            this.$addLink = this.$editor.find('.addLink');
            this.datasets = designer.getDatasets();
            this._restrictedDatasets = designer.getRestrictedDatasets();

            // The query as loaded, snapshotted by the designer - but only for roots bound to a restricted
            // dataset. It keeps those restricted datasets selectable, and is what a revert restores.
            const loadedQuery = designer.getRootSnapshot(this.component.id, this.propertyRoot)?.value as IDataQuery | undefined;
            if (loadedQuery) {
                this._loadedQueryDatasetIds = new Set(getDatasetRefsInDataQuery(loadedQuery).map(r => r.id));

                // A restricted dataset's query is read-only for the user - hold exclusive write ownership of
                // the shared root so the hidden editors can't corrupt it (see _guardAndRestoreQuery)
                this._syncQueryGuard(loadedQuery);
            }
        }

        // Keeps the read-only guard in step with the query: held while any ref is restricted, released otherwise.
        // The guard blocks the other (hidden) editors; this editor is the holder, so its own writes go through.
        private _syncQueryGuard(dataQuery: IDataQuery | null): void {
            if (this.isDataQueryRestricted(dataQuery)) {
                if (!this._queryGuard)
                    this._queryGuard = this.guardMyPath();
            }
            else if (this._queryGuard) {
                this._queryGuard.release();
                this._queryGuard = null;
            }
        }

        protected setValue(newValue: any): void {
            super.setValue(newValue);
            this._syncQueryGuard(this.getValue() as IDataQuery);
        }

        // Guard BEFORE restoring: restore() publishes the property update synchronously, so the hidden data
        // editors (e.g. metrics) run their update() inside that call and would otherwise write to the query
        // and clobber the value we just restored. Holding the guard first turns those writes into no-ops,
        // while the guard's holder (this editor) is still allowed to write the restored value back.
        private _guardAndRestoreQuery(): void {
            this._queryGuard?.release();
            this._queryGuard = this.guardMyPath();
            designer.getRootSnapshot(this.component.id, this.propertyRoot)?.restore();
        }

        private isDataQueryRestricted(dataQuery: IDataQuery | null): boolean {
            if (!dataQuery?.primaryDataset?.id)
                return false;

            if (designer.isRestricted(dataQuery.primaryDataset.id))
                return true;

            return (dataQuery.links || []).some(link => designer.isRestricted(link.linkedDataset?.id));
        }

        update(): void {
            const dataQuery = this.getValue() as IDataQuery;
            const datasets= this.datasets;

            // Update Primary Dataset and Alias
            this.$datasetSelect.empty().append('<option value="">Choose dataset...</option>');
            datasets.forEach(t => {
                const $opt = $('<option>').val(t.id);
                buildDatasetOption($opt, t);
                this.$datasetSelect.append($opt);
            });

            const boundRestrictedIds = new Set(getDatasetRefsInDataQuery(dataQuery).map(r => r.id));
            this._restrictedDatasets.filter(t => this._loadedQueryDatasetIds.has(t.id) || boundRestrictedIds.has(t.id)).forEach(t => {
                const $opt = $('<option>').val(t.id).attr('data-restricted', 'true');
                buildDatasetOption($opt, t, true);
                this.$datasetSelect.append($opt);
            });
            this.$datasetSelect.val(dataQuery?.primaryDataset?.id || '');

            const isRestricted = this.isDataQueryRestricted(dataQuery);

            // The alias is meaningless for a restricted dataset (its config is read-only), so hide it
            this.$aliasContainer.toggle(!!dataQuery?.primaryDataset?.id && !isRestricted);

            if (dataQuery?.primaryDataset?.id) {
                this.$aliasInput.val(dataQuery.primaryDataset.alias || '').prop('disabled', isRestricted);
                this.$aliasInput.attr('placeholder', 'Alias (Optional)');
            }
            else {
                this.$aliasInput.val('').prop('disabled', true);
                this.$aliasInput.attr('placeholder', 'Alias');
            }

            // Update Links
            const links = dataQuery?.links || [];
            while (this.linkEditors.length > links.length)
                this.linkEditors.pop()?.destroy();

            const datasetRefs: IDatasetReference[] = [];
            if (dataQuery?.primaryDataset?.id)
                datasetRefs.push({ ...dataQuery.primaryDataset });

            // Restricted datasets already present in this session's query stay selectable in the "To" dropdown
            const allowedRestrictedIds = new Set([...this._loadedQueryDatasetIds, ...boundRestrictedIds]);

            links.forEach((link, index) => {
                if (this.linkEditors.length <= index)
                    this.linkEditors.push(new DatasetLinkEditor(this.$linksList));

                const isFirstLink = index === 0;
                this.linkEditors[index].update({
                    linkData: link,
                    datasets: datasets,
                    datasetRefs: [...datasetRefs], // Pass context *up to* this link
                    restrictedDatasetIds: allowedRestrictedIds,
                    onInput: (newLinkData) => {
                        const dataQuery = this.getValueForEdit<IDataQuery>();
                        const oldAlias = dataQuery.links![index].linkedDataset.alias;
                        dataQuery.links![index] = newLinkData;

                        this.setValue(dataQuery);

                        if (oldAlias !== newLinkData.linkedDataset.alias) {
                            eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                                componentId: this.component.id,
                                oldAlias: oldAlias,
                                newAlias: newLinkData.linkedDataset.alias
                            });
                        }
                    },
                    onChanged: (newLinkData, forceAliasCheck = false) => {
                        const dataQuery = this.getValueForEdit<IDataQuery>();
                        const oldLinkDatasetId = dataQuery.links![index].linkedDataset.id;
                        const oldAlias = dataQuery.links![index].linkedDataset.alias;

                        dataQuery.links![index] = newLinkData;

                        // Reverting a link to its loaded restricted dataset restores the whole loaded query -
                        // the restricted dataset's config (columns, alias, joins) must stay exactly as loaded.
                        // Only a dataset change triggers this; the relationship alias stays editable.
                        const loadedQuery = designer.getRootSnapshot(this.component.id, this.propertyRoot)?.value as IDataQuery | undefined;
                        if (oldLinkDatasetId !== newLinkData.linkedDataset.id
                            && designer.isRestricted(newLinkData.linkedDataset.id)
                            && loadedQuery?.links?.some(l => l.linkedDataset?.id === newLinkData.linkedDataset.id)) {
                            this._guardAndRestoreQuery();
                            return;
                        }

                        // If this link's dataset ID changed, invalidate all further links that depended on it
                        if (oldLinkDatasetId !== newLinkData.linkedDataset.id) {
                            const thisLinkContextIndex = index + 1; // 0 is primary, 1 is links[0], etc.
                            for (let k = index + 1; k < dataQuery.links!.length; k++) {
                                if (dataQuery.links[k].primaryDatasetIndex === thisLinkContextIndex) {
                                    dataQuery.links[k].primaryDatasetIndex = undefined;
                                    dataQuery.links[k].primaryColumnId = '';
                                }
                            }
                        }

                        // Run de-duplication on the alias that just changed
                        this._runAliasDeduplication(dataQuery, newLinkData.linkedDataset, forceAliasCheck);
                        this.setValue(dataQuery);

                        if (oldAlias !== newLinkData.linkedDataset.alias) {
                            eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                                componentId: this.component.id,
                                oldAlias: oldAlias,
                                newAlias: newLinkData.linkedDataset.alias
                            });
                        }
                    },
                    onDelete: () => {
                        const dataQuery = this.getValueForEdit<IDataQuery>();
                        dataQuery.links!.splice(index, 1);

                        // Invalidate all subsequent links that depended on the deleted link
                        const deletedLinkContextIndex = index + 1;
                        for (let k = index; k < dataQuery.links!.length; k++) { // Start from the current index
                            if (dataQuery.links[k].primaryDatasetIndex === deletedLinkContextIndex) {
                                dataQuery.links[k].primaryDatasetIndex = undefined;
                                dataQuery.links[k].primaryColumnId = '';
                            }
                            // Also shift down the index for any links that pointed *after* the deleted one
                            if (appn.utils.hasValue(dataQuery.links[k].primaryDatasetIndex) && dataQuery.links[k].primaryDatasetIndex! > deletedLinkContextIndex)
                                dataQuery.links[k].primaryDatasetIndex!--;
                        }

                        this.setValue(dataQuery);
                    }
                }, isFirstLink);

                // Add this link's dataset to the context for the *next* link
                if (link.linkedDataset.id) {
                    datasetRefs.push({
                        id: link.linkedDataset.id,
                        alias: link.linkedDataset.alias
                    });
                }
            });

            // Update "Add Link" button visibility
            // Link is "well defined" if it has a dataset and both columns
            // For first link (index 0), primaryDatasetIndex can be undefined (defaults to primary)
            const isLinkWellDefined = (link: IDatasetLink, linkIndex: number) => {
                const hasToDataset = !!link.linkedDataset.id;
                const hasToColumn = !!link.linkedColumnId;
                const hasFromColumn = !!link.primaryColumnId;
                // First link implicitly has primaryDatasetIndex = 0
                const hasFromDataset = linkIndex === 0 || appn.utils.hasValue(link.primaryDatasetIndex);
                return hasToDataset && hasToColumn && hasFromDataset && hasFromColumn;
            };
            
            // All *existing* links must be well-defined to add a new one
            const allLinksWellDefined = (dataQuery?.links || []).every((link, idx) => isLinkWellDefined(link, idx));
            const isDatasetSelected = !!dataQuery?.primaryDataset?.id;
            // Links can still be added while a link is restricted - only a restricted primary blocks it
            const primaryRestricted = designer.isRestricted(dataQuery?.primaryDataset?.id);
            this.$addLink.toggle(!primaryRestricted && isDatasetSelected && allLinksWellDefined);

            // Init select2 now that all data is populated
            // Icon-text selects (dataset selectors) show origin icons and hierarchy
            appn.select.initForDesign({
                elements: this.$editor.find('.primaryDsSelect, .primaryDatasetSelect, .linkedDatasetSelect'),
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH,
                isIconTextSelect: true,
                isHierarchical: true
            });
            // Standard selects for everything else
            appn.select.initForDesign({
                elements: this.$editor.find('select').not('.primaryDsSelect, .linkedDatasetSelect'),
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.$editor.on('change', '.primaryDsSelect', e => {
                const datasetId = $(e.target).val() as string;
                const dataset = this.datasets.find(t => t.id === datasetId) || this._restrictedDatasets.find(t => t.id === datasetId);

                if (!dataset) {
                    this.setValue(null); // Clear the whole dataset if "Choose dataset..." is selected
                    return;
                }

                // Reverting to the restricted dataset as loaded restores its original config wholesale -
                // props changed while another dataset was selected must not leak into it
                const loadedQuery = designer.getRootSnapshot(this.component.id, this.propertyRoot)?.value as IDataQuery | undefined;
                if (designer.isRestricted(dataset.id) && loadedQuery?.primaryDataset?.id === dataset.id) {
                    this._guardAndRestoreQuery();
                    return;
                }

                // Get the existing dataset or create a new one
                const dataQuery = this.getValueForEdit<IDataQuery>() || {
                    name: 'default',
                    primaryDataset: { id: '', alias: '' },
                    links: [],
                    filter: null,
                    aggregation: null,
                    aggregationFilter: null,
                    projection: null,
                    sortOrders: [],
                    limit: null,
                    offset: null
                };
                
                if (!dataQuery.primaryDataset)
                    dataQuery.primaryDataset = { id: '', alias: '' };

                const oldDatasetId = dataQuery.primaryDataset.id;

                // Update primary dataset info
                dataQuery.primaryDataset.id = dataset.id;

                // If the primary dataset *ID* changed, invalidate dependent links and reset alias
                if (oldDatasetId !== dataset.id) {
                    dataQuery.primaryDataset.alias = '';
                    if (dataQuery.links) {
                        dataQuery.links.forEach(link => {
                            // If a link was pointing to primary (index 0), invalidate it
                            if (link.primaryDatasetIndex === 0) {
                                link.primaryDatasetIndex = undefined;
                                link.primaryColumnId = '';
                            }
                        });
                    }
                }

                this.setValue(dataQuery);
            });

            this.$editor.on('input', '.primaryDsAlias', e => {
                const dataQuery = this.getValueForEdit<IDataQuery>();
                if (dataQuery?.primaryDataset) {
                    const oldAlias = dataQuery.primaryDataset.alias;
                    const newAlias = $(e.target).val() as string;
                    dataQuery.primaryDataset.alias = newAlias;

                    this.setValue(dataQuery);

                    if (oldAlias !== newAlias) {
                        eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                            componentId: this.component.id,
                            oldAlias: oldAlias,
                            newAlias: newAlias
                        });
                    }
                }
            });

            this.$editor.on('change', '.primaryDsAlias', e => {
                const dataQuery = this.getValueForEdit<IDataQuery>();
                if (dataQuery?.primaryDataset) {
                    dataQuery.primaryDataset.alias = $(e.target).val() as string;
                    // Run de-duplication on blur (forceCheck = false)
                    this._runAliasDeduplication(dataQuery, dataQuery.primaryDataset, false);
                    this.setValue(dataQuery);
                }
            });

            this.$editor.on('click', '.addLink', () => {
                const dataQuery = this.getValueForEdit<IDataQuery>();
                if (!dataQuery.links)
                    dataQuery.links = [];

                // First link implicitly joins from primary dataset (index 0)
                const isFirstLink = dataQuery.links.length === 0;
                const primaryDatasetIndex = isFirstLink ? 0 : undefined;

                dataQuery.links.push({
                    linkedDataset: { id: '', alias: '' },
                    linkedColumnId: '',
                    primaryDatasetIndex: primaryDatasetIndex,
                    primaryColumnId: ''
                });
                this.setValue(dataQuery);
            });
        }

        private _runAliasDeduplication(dataQuery: IDataQuery, changedDatasetRef: IDatasetReference, forceCheckBlankAlias: boolean = false): void {
            const datasetRefs = [dataQuery.primaryDataset, ...dataQuery.links?.map(l => l.linkedDataset)];
            const allOtherDatasets = datasetRefs.filter(t => t !== changedDatasetRef);

            // Get all *defined* aliases from other datasets
            const allOtherAliases = new Set(allOtherDatasets.map(t => t.alias).filter(a => a));

            // Get all *names* from other datasets (resolved by id)
            const allOtherNames = new Set(allOtherDatasets.map(t => getDatasetRefName(t)).filter(n => n));

            let currentAlias = changedDatasetRef.alias;
            if (currentAlias) {
                // Case 1: User-defined alias. De-dupe against other aliases and names.
                let finalAlias = currentAlias;
                let counter = 1;
                while (allOtherAliases.has(finalAlias) || allOtherNames.has(finalAlias)) {
                    finalAlias = `${currentAlias}_${counter++}`;
                }
                changedDatasetRef.alias = finalAlias;

            } else if (forceCheckBlankAlias) {
                // Case 2: Blank alias, forced check (from .linkedColumnSelect).
                // Check if our *name* conflicts with any other alias OR name.
                const changedDatasetName = getDatasetRefName(changedDatasetRef);
                const conflictsWithAlias = allOtherAliases.has(changedDatasetName);
                const conflictsWithName = allOtherNames.has(changedDatasetName);

                if (conflictsWithAlias || conflictsWithName) {
                    // Conflict found! Generate a new alias.
                    let baseAlias = changedDatasetName;
                    let finalAlias = baseAlias;
                    let counter = 1;
                    // Find a name unique against *all* other aliases and names
                    while (allOtherAliases.has(finalAlias) || allOtherNames.has(finalAlias))
                        finalAlias = `${baseAlias}_${counter++}`;
                    changedDatasetRef.alias = finalAlias;
                }
                // If no conflict, the alias remains blank, which is correct.
            }
            // Case 3: Blank alias, Do nothing.
        }

        destroy(): void {
            this._queryGuard?.release();
            this._queryGuard = null;
            this.linkEditors.forEach(editor => editor.destroy());
            this.linkEditors = [];
            super.destroy();
        }
    }

    type FilterValueType = 'static' | 'dynamic' | 'column' | 'globalFilter';

    /** Global filter types whose values a criterion operator can take ("range compatible operators"). */
    const GLOBAL_FILTER_TYPES_BY_OPERATOR: { [op: string]: DashboardGlobalFilterType[] } = {
        'Equals': [DashboardGlobalFilterType.Text], 'NotEquals': [DashboardGlobalFilterType.Text],
        'Contains': [DashboardGlobalFilterType.Text], 'NotContains': [DashboardGlobalFilterType.Text],
        'StartsWith': [DashboardGlobalFilterType.Text], 'NotStartsWith': [DashboardGlobalFilterType.Text],
        'EndsWith': [DashboardGlobalFilterType.Text], 'NotEndsWith': [DashboardGlobalFilterType.Text],
        'In': [DashboardGlobalFilterType.Dropdown], 'NotIn': [DashboardGlobalFilterType.Dropdown],
        'Between': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange],
        'NotBetween': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange],
        'GreaterThan': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange],
        'GreaterThanOrEqual': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange],
        'LessThan': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange],
        'LessThanOrEqual': [DashboardGlobalFilterType.DateRange, DashboardGlobalFilterType.NumberRange]
    };

    function getGlobalFiltersForValueType(operator: string | undefined, numericTarget: boolean): IDashboardGlobalFilter[] {
        const allowedTypes: DashboardGlobalFilterType[] = numericTarget
            ? [DashboardGlobalFilterType.NumberRange]
            : (operator ? GLOBAL_FILTER_TYPES_BY_OPERATOR[operator] : undefined) || [];
        return ((designer._design as IDashboardDesign | null)?.globalFilters || [])
            .filter(f => allowedTypes.includes(f.type));
    }

    interface IDateTimePicker {
        date(): any;
        date(date: any): void;
        clear(): void;
        useCurrent(useCurrent: boolean | string): void;
    }

    interface IFilterCriterionValueEditorContext {
        currentColumnType: string | null;
        datasetRefs: IDatasetReference[];
        compatibleTypes: string[];
        value: string | undefined;
        dynamicValue: string | undefined;
        datasetIndex: number | undefined;
        columnId: string | undefined;

        /** The criterion's operator — decides which global filters can feed this value slot. */
        operator: string | undefined;
        globalFilterId: string | undefined;
    }

    class FilterCriterionValueEditor {
        private $root: JQuery;
        private $staticGroup: JQuery;
        private $dynamicGroup: JQuery;
        private $columnGroup: JQuery;
        private $globalFilterGroup: JQuery;
        private $valueText: JQuery;
        private $valueNumeric: JQuery;
        private $valueDate: JQuery;
        private $dynamicInput: JQuery;
        private $columnSelect: JQuery;
        private $globalFilterSelect: JQuery;

        private valueType: FilterValueType = 'static';
        private hasLoadedValueType = false;
        private onValueChanged: (updates: Partial<IDataQueryFilter>) => void;
        
        private context: IFilterCriterionValueEditorContext;

        constructor(container: JQuery, onValueChanged: (updates: Partial<IDataQueryFilter>) => void) {
            this.$root = appn.utils.cloneTemplate('editor-filterCriterionValue');
            container.append(this.$root);

            this.onValueChanged = onValueChanged;
            this.context = {
                currentColumnType: null,
                datasetRefs: [],
                compatibleTypes: [],
                value: undefined,
                dynamicValue: undefined,
                datasetIndex: undefined,
                columnId: undefined,
                operator: undefined,
                globalFilterId: undefined
            };

            this.$staticGroup = this.$root.find('.value-static-group');
            this.$dynamicGroup = this.$root.find('.value-dynamic-group');
            this.$columnGroup = this.$root.find('.value-column-group');
            this.$globalFilterGroup = this.$root.find('.value-globalfilter-group');
            this.$valueText = this.$root.find('.filterValueText');
            this.$valueNumeric = this.$root.find('.filterValueNumeric');
            this.$valueDate = this.$root.find('.filterValueDate');
            this.$dynamicInput = this.$root.find('.filterDynamicValue');
            this.$columnSelect = this.$root.find('.filterValueColumnSelect');
            this.$globalFilterSelect = this.$root.find('.filterGlobalFilterSelect');

            this.initValueTypeSelects();
            this.initDatePicker();
            this.bindEvents();
        }

        private get datePicker(): IDateTimePicker | null {
            return (this.$valueDate.data('DateTimePicker') as IDateTimePicker) || null;
        }

        private initDatePicker(): void {
            appn.ui.dateTimePicker(this.$valueDate, () => this.onDatePickerChanged());

            // Default to today 00:00 as date filters are typically used for whole days
            const picker = this.datePicker;
            if (picker)
                picker.useCurrent('day');
        }

        private onDatePickerChanged(): void {
            const selectedMoment = this.datePicker?.date();
            let finalValue: string | undefined;

            if (selectedMoment && (typeof selectedMoment.isValid === 'function' ? selectedMoment.isValid() : selectedMoment))
                finalValue = selectedMoment.format('YYYY-MM-DDTHH:mm:ss') + 'Z';

            this.context.value = finalValue;
            this.onValueChanged({ value: finalValue });
        }

        private setDatePickerValue(value?: string | null): void {
            const picker = this.datePicker;
            if (!picker) {
                this.$valueDate.val(value?.trim() ?? '');
                return;
            }

            const dateVal = value?.trim();
            if (!dateVal) {
                picker.clear();
                return;
            }

            // Stored values are UTC ('yyyy-MM-ddTHH:mm:ssZ'). Parse as UTC and hand the picker a
            // moment: it renders the moment's own fields, so the stored UTC wall-clock is displayed
            // as-is (no local-time shift). A raw string cannot be passed — the picker parses strings
            // against its display format ('DD-MMM-YYYY HH:mm'), which rejects ISO 8601.
            const parsed = (window as any).moment.utc(dateVal);
            if (parsed.isValid())
                picker.date(parsed);
            else
                picker.clear();
        }

        public update(context: IFilterCriterionValueEditorContext): void {
            this.context = context;
            
            if (!this.hasLoadedValueType) {
                if (this.context.globalFilterId)
                    this.valueType = 'globalFilter';
                else if (this.context.datasetIndex != null && this.context.columnId)
                    this.valueType = 'column';
                else if (this.context.dynamicValue)
                    this.valueType = 'dynamic';
                else
                    this.valueType = 'static';
                
                this.hasLoadedValueType = true;
            }

            this.renderUI();
        }

        public show(): void {
            this.$root.show();
        }

        public hide(): void {
            this.$root.hide();
        }

        private renderUI(): void {
            this.$root.find('.valueTypeSelect')
                .val(this.valueType)
                .trigger('change.select2');

            this.$staticGroup.toggle(this.valueType === 'static');
            this.$dynamicGroup.toggle(this.valueType === 'dynamic');
            this.$columnGroup.toggle(this.valueType === 'column');
            this.$globalFilterGroup.toggle(this.valueType === 'globalFilter');

            this.$valueText.hide();
            this.$valueNumeric.hide();
            this.$valueDate.hide();

            if (this.valueType === 'globalFilter')
                this.populateGlobalFilterSelect();
            else if (this.valueType === 'static') {
                const columnType = this.context.currentColumnType;
                let $input: JQuery;
                if (columnType === 'Number')
                    $input = this.$valueNumeric;
                else if (columnType === 'Date')
                    $input = this.$valueDate;
                else
                    $input = this.$valueText;
                
                $input.show();

                const value = this.context.value;
                if (columnType === 'Date')
                    this.setDatePickerValue(value || '');
                else
                    $input.val(value || '');
            } else if (this.valueType === 'dynamic')
                this.$dynamicInput.val(this.context.dynamicValue || '');
            else if (this.valueType === 'column'){
                const currentValue = this.context.datasetIndex != null && this.context.columnId
                    ? `${this.context.datasetIndex}|${this.context.columnId}` : '';
                const typeFilter = this.context.compatibleTypes.length > 0
                    ? this.context.compatibleTypes
                    : null;

                populatePrefixedColumnSelect(this.$columnSelect, this.context.datasetRefs, typeFilter, currentValue);
            }
        }

        private initValueTypeSelects(): void {
            appn.select.initForDesign({
                elements: this.$root.find('.valueTypeSelect'),
                isIconButtonSelect: true,
                omitWidth: true
            });

            this.$root.find('.valueTypeSelect').on('select2:select', (e: any) => {
                const newType = e.params.data.id as FilterValueType;
                if (newType !== this.valueType) {
                    this.valueType = newType;
                    this.renderUI();
                }
            });
        }

        private openFormulaBuilder(): void {
            const currentValue = this.context.dynamicValue;

            appn.ui.formulabuilder.controlName = 'FilterValue';
            appn.ui.formulabuilder.$input = this.$dynamicInput;
            appn.ui.formulabuilder.currentFormula = currentValue || '';
            appn.ui.formulabuilder.currentMode = appn.designerv2?.FormulaType?.normal;
            appn.ui.formulabuilder.isFormula = true;
            appn.ui.formulabuilder.formulaUpdated = (formula: string) => {
                this.$dynamicInput.val(formula).trigger('input');
            };
            appn.ui.formulabuilder.showDialog();
        }

        private bindEvents(): void {
            this.$root.on('input change', '.filterValueText, .filterValueNumeric', (e) => {
                const target = $(e.currentTarget);
                let rawValue = target.val() as string;
                let finalValue: string | undefined;

                if (!rawValue || rawValue === '')
                    finalValue = undefined;
                else
                    finalValue = rawValue;

                this.context.value = finalValue;
                this.onValueChanged({ value: finalValue });
            });

            this.$root.on('change', '.filterDynamicValue', (e) => {
                const val = $(e.target).val() as string;
                this.context.dynamicValue = val || undefined;
                this.onValueChanged({ dynamicValue: val || undefined });
            });

            this.$root.on('click', '.buildDynamicIcon', (e) => {
                e.preventDefault();
                this.openFormulaBuilder();
            });

            this.$root.on('change', '.filterValueColumnSelect', (e) => {
                const val = $(e.target).val() as string;

                if (val) {
                    const [datasetIndex, columnId] = val.split('|');
                    this.context.datasetIndex = parseInt(datasetIndex, 10);
                    this.context.columnId = columnId;
                    this.onValueChanged({ datasetIndex: parseInt(datasetIndex, 10), columnId: columnId });
                } else {
                    this.context.datasetIndex = undefined;
                    this.context.columnId = undefined;
                    this.onValueChanged({ datasetIndex: undefined, columnId: undefined });
                }
            });

            this.$root.on('change', '.filterGlobalFilterSelect', (e) => {
                const val = $(e.target).val() as string;
                this.context.globalFilterId = val || undefined;
                this.onValueChanged({ globalFilterId: val || undefined });
            });
        }

        private populateGlobalFilterSelect(): void {
            // Only filters whose values fit the criterion's operator are offered.
            const compatible = getGlobalFiltersForValueType(this.context.operator, false);
            const current = this.context.globalFilterId || '';

            this.$globalFilterSelect.empty().append(`<option value="">${T('Choose global filter')}...</option>`);
            compatible.forEach(f => this.$globalFilterSelect.append($('<option>').val(f.id).text(f.label || f.id)));

            appn.select.initForDesign({
                elements: this.$globalFilterSelect.not('.select2-hidden-accessible'),
                omitWidth: true,
                minimumResultsForSearch: '10'
            });

            this.$globalFilterSelect.val(compatible.some(f => f.id === current) ? current : '')
                .trigger('change.select2');
        }

        public destroy(): void {
            this.$root.remove();
        }
    }

    class FilterCriterionEditor {
        public static readonly defaultOperatorMap: { [type: string]: string } = {
            'Number': 'Equals',
            'Date': 'GreaterThan',
            'Text': 'Equals',
            'LongText': 'Equals',
            'Boolean': 'Equals',
            'UniqueIdentifier': 'Equals'
        };
        public static readonly operatorRequirements: { [op: string]: { values: 0 | 1 | 2; types: string[] } } = {
            'Equals': { values: 1, types: ['Text', 'LongText', 'Number', 'Boolean', 'UniqueIdentifier'] },
            'NotEquals': { values: 1, types: ['Text', 'LongText', 'Number', 'Boolean', 'UniqueIdentifier'] },
            'GreaterThan': { values: 1, types: ['Number', 'Date'] },
            'GreaterThanOrEqual': { values: 1, types: ['Number', 'Date'] },
            'LessThan': { values: 1, types: ['Number', 'Date'] },
            'LessThanOrEqual': { values: 1, types: ['Number', 'Date'] },
            'Between': { values: 2, types: ['Number', 'Date'] },
            'NotBetween': { values: 2, types: ['Number', 'Date'] },
            'Contains': { values: 1, types: ['Text', 'LongText'] },
            'NotContains': { values: 1, types: ['Text', 'LongText'] },
            'StartsWith': { values: 1, types: ['Text', 'LongText'] },
            'NotStartsWith': { values: 1, types: ['Text', 'LongText'] },
            'EndsWith': { values: 1, types: ['Text', 'LongText'] },
            'NotEndsWith': { values: 1, types: ['Text', 'LongText'] },
            'IsBlank': { values: 0, types: ['Text', 'LongText', 'Number', 'Date', 'Boolean', 'UniqueIdentifier'] },
            'NotIsBlank': { values: 0, types: ['Text', 'LongText', 'Number', 'Date', 'Boolean', 'UniqueIdentifier'] },
            'In': { values: 1, types: ['Text', 'LongText', 'Number', 'UniqueIdentifier'] },
            'NotIn': { values: 1, types: ['Text', 'LongText', 'Number', 'UniqueIdentifier'] }
        };

        public static cleanFilterCriterion(criterion: IDataQueryFilter, columnType: string | null): IDataQueryFilter {
            const cleaned = { ...criterion };

            // A global filter criterion's values are request-scoped — no stored value slots.
            if (cleaned.globalFilterId) {
                cleaned.value = undefined;
                cleaned.dynamicValue = undefined;
                cleaned.valueDatasetIndex = undefined;
                cleaned.valueColumnId = undefined;
                cleaned.value2 = undefined;
                cleaned.dynamicValue2 = undefined;
                cleaned.value2DatasetIndex = undefined;
                cleaned.value2ColumnId = undefined;
                return cleaned;
            }

            const operator = cleaned.operator || 'Equals';
            const req = FilterCriterionEditor.operatorRequirements[operator];
            const valuesNeeded = req ? req.values : 0;

            interface IValueSlot {
                value?: string;
                dynamicValue?: string;
                datasetIndex?: number;
                columnId?: string;
            }
            const normaliseValueSlot = (valueSlot: IValueSlot): IValueSlot => {
                const { value, dynamicValue, datasetIndex, columnId } = valueSlot;

                // Explicitly check dataset index against null (and undefined with loose '!=' comparison)
                // datasetIndex can be 0, which is falsey but valid
                if (datasetIndex != null && columnId)
                    return { datasetIndex, columnId };
                
                if (dynamicValue)
                    return { dynamicValue };
                
                return { value };
            };

            if (valuesNeeded >= 1) {
                const valueSlot = normaliseValueSlot({
                    value: cleaned.value,
                    dynamicValue: cleaned.dynamicValue,
                    datasetIndex: cleaned.valueDatasetIndex,
                    columnId: cleaned.valueColumnId
                });
                cleaned.value = valueSlot.value;
                cleaned.dynamicValue = valueSlot.dynamicValue;
                cleaned.valueDatasetIndex = valueSlot.datasetIndex;
                cleaned.valueColumnId = valueSlot.columnId;
            } else {
                cleaned.value = undefined;
                cleaned.dynamicValue = undefined;
                cleaned.valueDatasetIndex = undefined;
                cleaned.valueColumnId = undefined;
            }

            if (valuesNeeded >= 2) {
                const value2Slot = normaliseValueSlot({
                    value: cleaned.value2,
                    dynamicValue: cleaned.dynamicValue2,
                    datasetIndex: cleaned.value2DatasetIndex,
                    columnId: cleaned.value2ColumnId
                });
                
                cleaned.value2 = value2Slot.value;
                cleaned.dynamicValue2 = value2Slot.dynamicValue;
                cleaned.value2DatasetIndex = value2Slot.datasetIndex;
                cleaned.value2ColumnId = value2Slot.columnId;
            } else {
                cleaned.value2 = undefined;
                cleaned.dynamicValue2 = undefined;
                cleaned.value2DatasetIndex = undefined;
                cleaned.value2ColumnId = undefined;
            }

            if (columnType && req && !req.types.includes(columnType)) {
                const defaultOp = FilterCriterionEditor.defaultOperatorMap[columnType];
                if (defaultOp && FilterCriterionEditor.operatorRequirements[defaultOp]?.types.includes(columnType))
                    cleaned.operator = defaultOp;
                else {
                    for (const opKey in FilterCriterionEditor.operatorRequirements) {
                        const opReq = FilterCriterionEditor.operatorRequirements[opKey];
                        if (opReq && opReq.types.includes(columnType)) {
                            cleaned.operator = opKey;
                            break;
                        }
                    }
                }
            }

            return cleaned;
        }

        private $filterCriterionRow: JQuery;
        private $removeCriterion: JQuery;
        private $column: JQuery;
        private $operator: JQuery;
        private $valueArea: JQuery;
        private $valuePlaceholder: JQuery;
        private $value2Row: JQuery;
        private $value1Container: JQuery;
        private $value2Container: JQuery;
        private $valueSeparator: JQuery;
        private $allOperatorOptions: JQuery;

        private valueEditor1: FilterCriterionValueEditor | null = null;
        private valueEditor2: FilterCriterionValueEditor | null = null;

        private criterion: IDataQueryFilter;
        private datasetRefs: IDatasetReference[];
        private availableColumns: { id: string; name: string; type: string }[] = [];
        private currentColumnType: string | null = null;
        private onChanged: (newData: IDataQueryFilter) => void;
        private onDelete: () => void;

        constructor(container: JQuery, criterion: IDataQueryFilter, datasetRefs: IDatasetReference[], onChanged: (d: IDataQueryFilter) => void, onDelete: () => void) {
            this.$filterCriterionRow = appn.utils.cloneTemplate('editor-filterCriterionRow');
            container.append(this.$filterCriterionRow);

            this.criterion = criterion;
            this.datasetRefs = datasetRefs;
            this.onChanged = onChanged;
            this.onDelete = onDelete;

            this.$column = this.$filterCriterionRow.find('.filterColumnSelect');
            this.$operator = this.$filterCriterionRow.find('.filterOperatorSelect');
            this.$removeCriterion = this.$filterCriterionRow.find('.removeBtn');

            this.$allOperatorOptions = this.$operator.find('option').clone();

            this.$valueArea = this.$filterCriterionRow.find('.value-input-area');
            this.$valuePlaceholder = this.$valueArea.find('.filterValuePlaceholder');
            this.$value1Container = this.$valueArea.find('.value1-input-container');
            this.$value2Row = this.$filterCriterionRow.find('.value2-row');
            this.$valueSeparator = this.$value2Row.find('.value-separator');
            this.$value2Container = this.$value2Row.find('.value2-input-container');

            this.update(criterion, datasetRefs);
            this.bindEvents();
        }

        public update(criterion: IDataQueryFilter, datasetRefs: IDatasetReference[], onChanged?: (d: IDataQueryFilter) => void): void {
            if (onChanged)
                this.onChanged = onChanged;
            this.criterion = criterion;
            this.datasetRefs = datasetRefs;

            const selectedValue = appn.utils.hasValue(this.criterion.datasetIndex) && this.criterion.columnId ? `${this.criterion.datasetIndex}|${this.criterion.columnId}` : '';
            this.populateColumns();
            this.$column.val(selectedValue);
            this.updateCurrentColumnType(selectedValue);
            this.populateOperators();
            this.$operator.val(this.criterion.operator || 'Equals');
            this.updateValueInputsVisibilityAndValues();
            this.$filterCriterionRow.toggleClass('filter-criterion-global', !!this.criterion.globalFilterId);
        }

        public showDeleteButton(visible: boolean): void {
            this.$removeCriterion.toggle(visible);
        }

        private populateColumns(): void {
            const selectedValue = appn.utils.hasValue(this.criterion.datasetIndex) && this.criterion.columnId ? `${this.criterion.datasetIndex}|${this.criterion.columnId}` : '';
            const populatedCols = populatePrefixedColumnSelect(this.$column, this.datasetRefs, null, selectedValue);

            this.availableColumns = populatedCols.map(c => ({ id: c.id, name: c.displayName, type: c.type }));
        }

        private updateCurrentColumnType(selectedValue: string): void {
            if (!selectedValue) {
                this.currentColumnType = null;
                return;
            }
            const [, columnId] = selectedValue.split('|');
            const columnMeta = this.availableColumns.find(c => c.id === columnId);
            this.currentColumnType = columnMeta?.type ?? null;
        }

        private populateOperators(): void {
            const currentOperator = this.$operator.val() as string;
            this.$operator.empty();

            const operators = this.$allOperatorOptions.clone();
            operators.each((_, opt) => {
                const $opt = $(opt);

                $opt.text($opt.text().toLowerCase());

                const opValue = $opt.val() as string;
                const req = FilterCriterionEditor.operatorRequirements[opValue];
                if (this.currentColumnType && req && req.types.includes(this.currentColumnType))
                    this.$operator.append($opt);
                else if (!this.currentColumnType)
                    this.$operator.append($opt);
            });

            if (this.$operator.find(`option[value="${currentOperator}"]`).length > 0)
                this.$operator.val(currentOperator);
            else
                this.$operator.val('Equals');
        }

        private getCompatibleColumnTypes(): string[] {
            const operator = this.$operator.val() as string;
            const req = FilterCriterionEditor.operatorRequirements[operator];

            if (!req || !this.currentColumnType)
                return [];

            if (['Equals', 'NotEquals', 'IsBlank', 'NotIsBlank'].includes(operator))
                return [this.currentColumnType];

            if (['Contains', 'NotContains', 'StartsWith', 'NotStartsWith', 'EndsWith', 'NotEndsWith'].includes(operator))
                return ['Text', 'LongText'];

            return req.types;
        }

        private updateValueInputsVisibilityAndValues(): void {
            const operator = this.$operator.val() as string;
            const req = FilterCriterionEditor.operatorRequirements[operator];
            // A global filter criterion's bounds both come from the filter — a single picker replaces both slots.
            const valuesNeeded = this.criterion.globalFilterId ? 1 : (req ? req.values : 0);

            this.$valuePlaceholder.hide();
            this.$value2Row.hide();
            this.$valueSeparator.hide();

            const createContextForValue1 = (): IFilterCriterionValueEditorContext => ({
                currentColumnType: this.currentColumnType,
                datasetRefs: this.datasetRefs,
                compatibleTypes: this.getCompatibleColumnTypes(),
                value: this.criterion.value,
                dynamicValue: this.criterion.dynamicValue,
                datasetIndex: this.criterion.valueDatasetIndex,
                columnId: this.criterion.valueColumnId,
                operator: this.criterion.operator,
                globalFilterId: this.criterion.globalFilterId
            });

            const createContextForValue2 = (): IFilterCriterionValueEditorContext => ({
                currentColumnType: this.currentColumnType,
                datasetRefs: this.datasetRefs,
                compatibleTypes: this.getCompatibleColumnTypes(),
                value: this.criterion.value2,
                dynamicValue: this.criterion.dynamicValue2,
                datasetIndex: this.criterion.value2DatasetIndex,
                columnId: this.criterion.value2ColumnId,
                operator: this.criterion.operator,
                globalFilterId: this.criterion.globalFilterId
            });

            if (valuesNeeded === 0) {
                this.$valuePlaceholder.show();
                if (this.valueEditor1)
                    this.valueEditor1.hide();
                if (this.valueEditor2)
                    this.valueEditor2.hide();
            } else if (valuesNeeded >= 1) {
                if (!this.valueEditor1) {
                    this.valueEditor1 = new FilterCriterionValueEditor(
                        this.$value1Container,
                        (updates) => {
                            this.criterion = { 
                                ...this.criterion, 
                                value: updates.value,
                                dynamicValue: updates.dynamicValue,
                                valueDatasetIndex: updates.datasetIndex,
                                valueColumnId: updates.columnId,
                                globalFilterId: updates.globalFilterId
                            };
                            this.criterion = FilterCriterionEditor.cleanFilterCriterion(this.criterion, this.currentColumnType);
                            this.onChanged(this.criterion);
                        }
                    );
                }
                this.valueEditor1.update(createContextForValue1());
                this.valueEditor1.show();
            }

            if (valuesNeeded === 2) {
                this.$value2Row.show();
                this.$valueSeparator.show();

                if (!this.valueEditor2) {
                    this.valueEditor2 = new FilterCriterionValueEditor(
                        this.$value2Container,
                        (updates) => {
                            this.criterion = { 
                                ...this.criterion, 
                                value2: updates.value,
                                dynamicValue2: updates.dynamicValue,
                                value2DatasetIndex: updates.datasetIndex,
                                value2ColumnId: updates.columnId
                            };
                            this.criterion = FilterCriterionEditor.cleanFilterCriterion(this.criterion, this.currentColumnType);
                            this.onChanged(this.criterion);
                        }
                    );
                }
                this.valueEditor2.update(createContextForValue2());
                this.valueEditor2.show();
            }
        }

        private bindEvents(): void {
            this.$filterCriterionRow.on('click', '.removeBtn', () => this.onDelete());

            this.$filterCriterionRow.on('change', '.filterColumnSelect', e => {
                const val = $(e.target).val() as string;
                
                this.criterion = { ...this.criterion, datasetIndex: undefined, columnId: undefined };
                
                if (val) {
                    const parts = val.split('|');
                    this.criterion.datasetIndex = parseInt(parts[0], 10);
                    this.criterion.columnId = parts[1];
                }

                this.updateCurrentColumnType(val);
                this.populateOperators();
                this.criterion.operator = this.$operator.val() as string;
                
                this.criterion = FilterCriterionEditor.cleanFilterCriterion(this.criterion, this.currentColumnType);
                this.onChanged(this.criterion);
            });

            this.$filterCriterionRow.on('change', '.filterOperatorSelect', e => {
                const newOperator = $(e.target).val() as string;

                // A global filter whose values don't fit the new operator can no longer feed the criterion.
                if (this.criterion.globalFilterId) {
                    const allowedTypes = GLOBAL_FILTER_TYPES_BY_OPERATOR[newOperator] || [];
                    const referenced = (((designer._design as IDashboardDesign | null)?.globalFilters) || [])
                        .find(f => f.id === this.criterion.globalFilterId);
                    if (!referenced || !allowedTypes.includes(referenced.type))
                        this.criterion = { ...this.criterion, globalFilterId: undefined };
                }

                this.criterion = { ...this.criterion, operator: newOperator };
                this.criterion = FilterCriterionEditor.cleanFilterCriterion(this.criterion, this.currentColumnType);
                this.onChanged(this.criterion);
            });
        }

        public destroy(): void {
            this.valueEditor1?.destroy();
            this.valueEditor2?.destroy();
            this.$filterCriterionRow.remove();
        }
    }
    class FilterGroupEditor {
        private $filterGroup: JQuery;
        private $operation: JQuery;
        private $conditions: JQuery;
        private $removeGroup: JQuery;
        private operationSelector: string;
        private filterGroup: IDataQueryFilter;
        private aliasedDatasets: IDatasetReference[];
        private childEditors: (FilterGroupEditor | FilterCriterionEditor)[] = [];
        private onChanged: (newData: IDataQueryFilter) => void;
        private onDelete: () => void;
        private isRoot: boolean;

        constructor(container: JQuery, filterGroup: IDataQueryFilter, aliasedDatasets: IDatasetReference[],
                    onChanged: (d: IDataQueryFilter) => void, onDelete: () => void, isRoot = false) {

            this.$filterGroup = isRoot ? container : appn.utils.cloneTemplate('editor-filterGroup');
            if (!isRoot)
                container.append(this.$filterGroup);

            this.isRoot = isRoot;
            this.filterGroup = filterGroup;
            this.aliasedDatasets = aliasedDatasets;
            this.onChanged = onChanged;
            this.onDelete = onDelete;
            this.operationSelector = isRoot ? '.filterLogicalOperator' : '.groupLogicalOperator';
            this.$operation = this.$filterGroup.find(this.operationSelector);
            this.$conditions = this.$filterGroup.find('.filterConditionsList');
            this.$removeGroup = this.$filterGroup.find('.removeGroupBtn');

            this.update(filterGroup, aliasedDatasets);
            this.bindEvents();
        }

        public update(filterGroup: IDataQueryFilter, aliasedDatasets: IDatasetReference[], onChanged?: (d: IDataQueryFilter) => void, onDelete?: () => void): void {
            if (onChanged)
                this.onChanged = onChanged;
            if (onDelete)
                this.onDelete = onDelete;
            this.filterGroup = filterGroup;
            this.aliasedDatasets = aliasedDatasets;
            this.$operation.val(this.filterGroup.groupOperator || 'AND');

            const newConditions = this.filterGroup.groupConditions || [];

            while (this.childEditors.length > newConditions.length)
                this.childEditors.pop()?.destroy();

            // A sub-group's children can only be deleted if the group has more than 2 items.
            // The root group's children can always be deleted.
            const canDeleteChildren = this.isRoot || newConditions.length > 2;
            newConditions.forEach((condition, index) => {
                const onChildChanged = (newChildData: IDataQueryFilter) => {
                    this.filterGroup = appn.utils.deepClone(this.filterGroup);
                    this.filterGroup.groupConditions![index] = newChildData;
                    this.onChanged(this.filterGroup);
                };
                const onChildDeleted = () => {
                    this.filterGroup = appn.utils.deepClone(this.filterGroup);
                    this.filterGroup.groupConditions!.splice(index, 1);

                    if (this.isRoot && this.filterGroup.groupConditions!.length === 0)
                        this.onDelete();
                    else
                        this.onChanged(this.filterGroup);
                };

                const existingEditor = this.childEditors[index];
                const isGroup = !!condition.groupConditions;

                const typeMatches = existingEditor
                    && ((isGroup && existingEditor instanceof FilterGroupEditor)
                        || (!isGroup && existingEditor instanceof FilterCriterionEditor));

                if (typeMatches)
                    (existingEditor as any).update(condition, this.aliasedDatasets, onChildChanged, onChildDeleted);
                else {
                    if (existingEditor)
                        existingEditor.destroy();

                    const $listItem = $('<li></li>');
                    this.$conditions.append($listItem);

                    if (isGroup) {
                        $listItem.addClass('mt-1');
                        this.childEditors[index] = new FilterGroupEditor($listItem, condition, this.aliasedDatasets, onChildChanged, onChildDeleted);
                    } else
                        this.childEditors[index] = new FilterCriterionEditor($listItem, condition, this.aliasedDatasets, onChildChanged, onChildDeleted);
                }

                const editor = this.childEditors[index];
                if (editor)
                    editor.showDeleteButton(canDeleteChildren);
            });
        }

        public showDeleteButton(visible: boolean): void {
            this.$removeGroup.toggle(visible);
        }

        private bindEvents(): void {
            const $removeEle = this.$filterGroup.parent().is('li') ? this.$filterGroup.parent() : this.$filterGroup;
            $removeEle.on('click', '.removeGroupBtn', (e) => {
                e.stopPropagation(); // Important so we don't propagate to root filter conditions
                this.onDelete()
            });
            this.$filterGroup.on('change', this.operationSelector, e => {
                this.filterGroup = { ...this.filterGroup, groupOperator: $(e.target).val() as any };
                this.onChanged(this.filterGroup);
            });
            this.$filterGroup.on('click', '.addCriterionToGroupBtn, .addFilterCriterion', (e) => {
                e.stopPropagation(); // Important so we don't propagate to root filter conditions
                this.addCondition(false);
            });
            this.$filterGroup.on('click', '.addGroupToGroupBtn, .addFilterGroup', (e)  => {
                e.stopPropagation(); // Important so we don't propagate to root filter conditions
                this.addCondition(true);
            });
        }

        private addCondition(isGroup: boolean): void {
            this.filterGroup = appn.utils.deepClone(this.filterGroup);
            if (!this.filterGroup.groupConditions)
                this.filterGroup.groupConditions = [];

            if (isGroup) {
                this.filterGroup.groupConditions.push({
                    groupOperator: 'OR',
                    groupConditions: [
                        { datasetIndex: 0, columnId: '', operator: 'Equals', value: '' },
                        { datasetIndex: 0, columnId: '', operator: 'Equals', value: '' } 
                    ]
                });
            } else
                this.filterGroup.groupConditions.push({datasetIndex: 0, columnId: '', operator: 'Equals', value: ''}); 

            this.onChanged(this.filterGroup);
        }

        public destroy(): void {
            this.childEditors.forEach(c => c.destroy());
            this.childEditors = [];
            if (!this.$filterGroup.parent().is('li'))
                this.$filterGroup.remove();
            else
                this.$filterGroup.parent().remove();
        }
    }
    class FiltersEditor extends DataQueryEditorBase {
        private $multiCriteriaHint!: JQuery;
        private rootGroupEditor: FilterGroupEditor | null = null;

        init() {
            this.$multiCriteriaHint = this.$editor.find('.filterMultiCriteriaHint');
        }

        update(): void {
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const originalFilters = this.getValue() as IDataQueryFilter | null;
            if (originalFilters) {
                const { cleanedNode, wasModified } = this.validateAndCleanFiltersRecurse(originalFilters, datasetRefs);
                if (wasModified) {
                    this.setValue(cleanedNode);
                    return;
                }
            }

            const filters = originalFilters || { groupOperator: 'AND', groupConditions: [] };
            if (this.rootGroupEditor)
                this.rootGroupEditor.update(filters, datasetRefs);
            else {
                const isRoot = true;
                const onChange = (newFilters: IDataQueryFilter) => this.setValue(newFilters);
                const onDelete = () => this.setValue(null);
                this.rootGroupEditor = new FilterGroupEditor(this.$editor, filters, datasetRefs, onChange, onDelete, isRoot);
            }
          
            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                omitWidth: true,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });

            if (this.$multiCriteriaHint.length > 0) {
                const criteriaCount = filters.groupConditions?.length || 0;
                this.$multiCriteriaHint.toggle(criteriaCount >= 2);
            }
        }

        bindEvents(): void { }

        destroy(): void {
            this.rootGroupEditor?.destroy();
            this.rootGroupEditor = null;
            super.destroy();
        }

        /**
         * Recursively traverses a filter node, cleaning invalid states (like incompatible operators/values)
         * but tolerating incomplete nodes.
         * @returns An object containing the cleaned node and a boolean indicating if changes were made.
         */
        private validateAndCleanFiltersRecurse(node: IDataQueryFilter | null, datasetRefs: IDatasetReference[]): { cleanedNode: IDataQueryFilter | null; wasModified: boolean } {
            if (!node) 
                return { cleanedNode: null, wasModified: false };

            let wasModified = false;
            let cleanedNode = appn.utils.deepClone(node);

            if (appn.utils.hasValue(cleanedNode.datasetIndex) && cleanedNode.columnId !== undefined) {
                const datasetRef = datasetRefs[cleanedNode.datasetIndex!];
                const dataset = datasetRef ? designer.getDatasets().find(t => t.id === datasetRef.id) : null;
                const columnMeta = dataset?.columns.find(c => c.id === cleanedNode.columnId);
                const columnType = columnMeta?.type ?? null;

                if (!columnMeta || !columnType) {
                    // Tolerate missing column meta for now in designer
                    return { cleanedNode: cleanedNode, wasModified: false };
                }

                const beforeClean = JSON.stringify(cleanedNode);
                cleanedNode = FilterCriterionEditor.cleanFilterCriterion(cleanedNode, columnType);
                const afterClean = JSON.stringify(cleanedNode);
                wasModified = beforeClean !== afterClean;

            } else if (cleanedNode.groupConditions) {
                let childWasModified = false;
                const newConditions: IDataQueryFilter[] = [];
                for (const condition of cleanedNode.groupConditions) {
                    const result = this.validateAndCleanFiltersRecurse(condition, datasetRefs);
                    if (result.cleanedNode)
                        newConditions.push(result.cleanedNode);
                    else
                        childWasModified = true;
                    
                    if (result.wasModified)
                        childWasModified = true;
                }

                if (childWasModified || newConditions.length !== cleanedNode.groupConditions.length) {
                    cleanedNode.groupConditions = newConditions;
                    wasModified = true;
                }
            } else {
                // Neither a valid leaf nor a valid group - Tolerate inconsistencies for now
                return { cleanedNode: cleanedNode, wasModified: false };
            }

            return { cleanedNode: cleanedNode, wasModified: wasModified };
        }
    }

    interface IAggregationFilterCriterionValueEditorContext {
        currentColumnType: string | null;
        aggregation: IDataQueryAggregation | null;
        datasetRefs: IDatasetReference[];
        value: string | undefined;
        dynamicValue: string | undefined;
        valueColumn: IDataQueryColumnIdentifier | undefined;
        valueMetricIndex: number | undefined;

        /** The criterion's operator — decides which global filters can feed this value slot. */
        operator: string | undefined;
        globalFilterId: string | undefined;
    }

    class AggregationFilterCriterionValueEditor {
        private $root: JQuery;
        private $staticGroup: JQuery;
        private $dynamicGroup: JQuery;
        private $columnGroup: JQuery;
        private $globalFilterGroup: JQuery;
        private $valueText: JQuery;
        private $valueNumeric: JQuery;
        private $valueDate: JQuery;
        private $dynamicInput: JQuery;
        private $columnSelect: JQuery;
        private $globalFilterSelect: JQuery;

        private valueType: FilterValueType = 'static';
        private hasLoadedValueType = false;
        private onValueChanged: (updates: Partial<IDataQueryAggregationFilter>) => void;
        
        private context: IAggregationFilterCriterionValueEditorContext;

        constructor(container: JQuery, onValueChanged: (updates: Partial<IDataQueryAggregationFilter>) => void) {
            this.$root = appn.utils.cloneTemplate('editor-aggregationFilterCriterionValue');
            container.append(this.$root);

            this.onValueChanged = onValueChanged;
            this.context = {
                currentColumnType: null,
                aggregation: null,
                datasetRefs: [],
                value: undefined,
                dynamicValue: undefined,
                valueColumn: undefined,
                valueMetricIndex: undefined,
                operator: undefined,
                globalFilterId: undefined
            };

            this.$staticGroup = this.$root.find('.value-static-group');
            this.$dynamicGroup = this.$root.find('.value-dynamic-group');
            this.$columnGroup = this.$root.find('.value-column-group');
            this.$globalFilterGroup = this.$root.find('.value-globalfilter-group');
            this.$valueText = this.$root.find('.filterValueText');
            this.$valueNumeric = this.$root.find('.filterValueNumeric');
            this.$valueDate = this.$root.find('.filterValueDate');
            this.$dynamicInput = this.$root.find('.filterDynamicValue');
            this.$columnSelect = this.$root.find('.filterValueColumnSelect');
            this.$globalFilterSelect = this.$root.find('.filterGlobalFilterSelect');

            this.initValueTypeSelects();
            this.initDatePicker();
            this.bindEvents();
        }

        private get datePicker(): IDateTimePicker | null {
            return (this.$valueDate.data('DateTimePicker') as IDateTimePicker) || null;
        }

        private initDatePicker(): void {
            appn.ui.dateTimePicker(this.$valueDate, () => this.onDatePickerChanged());

            // Default to today 00:00 as date filters are typically used for whole days
            const picker = this.datePicker;
            if (picker)
                picker.useCurrent('day');
        }

        private onDatePickerChanged(): void {
            const selectedMoment = this.datePicker?.date();
            let finalValue: string | undefined;

            if (selectedMoment && (typeof selectedMoment.isValid === 'function' ? selectedMoment.isValid() : selectedMoment))
                finalValue = selectedMoment.format('YYYY-MM-DDTHH:mm:ss') + 'Z';

            this.context.value = finalValue;
            this.onValueChanged({ value: finalValue });
        }

        private setDatePickerValue(value?: string | null): void {
            const picker = this.datePicker;
            if (!picker) {
                this.$valueDate.val(value?.trim() ?? '');
                return;
            }

            const dateVal = value?.trim();
            if (!dateVal) {
                picker.clear();
                return;
            }

            // Stored values are UTC ('yyyy-MM-ddTHH:mm:ssZ'). Parse as UTC and hand the picker a
            // moment: it renders the moment's own fields, so the stored UTC wall-clock is displayed
            // as-is (no local-time shift). A raw string cannot be passed — the picker parses strings
            // against its display format ('DD-MMM-YYYY HH:mm'), which rejects ISO 8601.
            const parsed = (window as any).moment.utc(dateVal);
            if (parsed.isValid())
                picker.date(parsed);
            else
                picker.clear();
        }

        public update(context: IAggregationFilterCriterionValueEditorContext): void {
            this.context = context;
            
            if (!this.hasLoadedValueType) {
                if (this.context.globalFilterId)
                    this.valueType = 'globalFilter';
                else if (this.context.valueColumn != null || this.context.valueMetricIndex != null)
                    this.valueType = 'column';
                else if (this.context.dynamicValue)
                    this.valueType = 'dynamic';
                else
                    this.valueType = 'static';
                
                this.hasLoadedValueType = true;
            }
            
            this.renderUI();
        }

        public show(): void {
            this.$root.show();
        }

        public hide(): void {
            this.$root.hide();
        }

        private renderUI(): void {
            this.$root.find('.valueTypeSelect')
                .val(this.valueType)
                .trigger('change.select2');

            this.$staticGroup.toggle(this.valueType === 'static');
            this.$dynamicGroup.toggle(this.valueType === 'dynamic');
            this.$columnGroup.toggle(this.valueType === 'column');
            this.$globalFilterGroup.toggle(this.valueType === 'globalFilter');

            this.$valueText.hide();
            this.$valueNumeric.hide();
            this.$valueDate.hide();

            if (this.valueType === 'globalFilter')
                this.populateGlobalFilterSelect();
            else if (this.valueType === 'static') {
                const columnType = this.context.currentColumnType;
                let $input: JQuery;
                if (columnType === 'Number')
                    $input = this.$valueNumeric;
                else if (columnType === 'Date')
                    $input = this.$valueDate;
                else
                    $input = this.$valueText;
                
                $input.show();

                const value = this.context.value;
                if (columnType === 'Date')
                    this.setDatePickerValue(value || '');
                else
                    $input.val(value || '');
            } else if (this.valueType === 'dynamic')
                this.$dynamicInput.val(this.context.dynamicValue || '');
            else if (this.valueType === 'column')
                this.populateColumnSelect();
        }

        private populateColumnSelect(): void {
            const aggregation = this.context.aggregation;
            const datasetRefs = this.context.datasetRefs;
            const datasets = designer.getDatasets();

            this.$columnSelect.empty().append('<option value="">Choose column...</option>');

            if (!aggregation)
                return;

            const { groupBy, breakDownBy, metrics } = aggregation;
            const groups: { label: string, value: string, type: string }[] = [];
            const showPrefix = datasetRefs.length > 1;

            const getGroupLabelAndMeta = (colIdentifier: IDataQueryColumnIdentifier | undefined, source: 'group' | 'breakdown'): { label: string, value: string, type: string } | null => {
                if (!colIdentifier)
                    return null;

                const { datasetRef, dataset, columnMeta } = getContextualColumnMeta(colIdentifier.datasetIndex, colIdentifier.columnId, datasetRefs, datasets);
                if (!datasetRef || !dataset || !columnMeta)
                    return null;

                const datasetPrefix = datasetRef.alias || dataset.name;
                const columnText = getColumnOptionText(columnMeta);
                const label = showPrefix ? `${datasetPrefix}.${columnText}` : columnText;
                const value = `${source}`;

                return { label, value, type: columnMeta.type };
            };

            const groupByMeta = getGroupLabelAndMeta(groupBy, 'group');
            if (groupByMeta)
                groups.push(groupByMeta);

            const breakDownByMeta = getGroupLabelAndMeta(breakDownBy, 'breakdown');
            if (breakDownByMeta)
                groups.push(breakDownByMeta);

            if (groups.length > 0) {
                const $optgroup = $('<optgroup label="Groups"></optgroup>');
                groups.forEach(g => $optgroup.append($('<option>').val(g.value).text(g.label)));
                this.$columnSelect.append($optgroup);
            }

            if (metrics && metrics.length > 0) {
                const $optgroup = $('<optgroup label="Metrics"></optgroup>');
                metrics.forEach((m, index) => {
                    const value = `metric|${index}`;
                    const text = getMetricDisplayText(m, index, aggregation, datasetRefs, datasets);
                    $optgroup.append($('<option>').val(value).text(text));
                });
                this.$columnSelect.append($optgroup);
            }

            // Set current value
            let currentValue = '';
            if (this.context.valueColumn != null) {
                // Match valueColumn to groupBy or breakDownBy
                if (groupBy && groupBy.datasetIndex === this.context.valueColumn.datasetIndex && groupBy.columnId === this.context.valueColumn.columnId)
                    currentValue = 'group';
                else if (breakDownBy && breakDownBy.datasetIndex === this.context.valueColumn.datasetIndex && breakDownBy.columnId === this.context.valueColumn.columnId)
                    currentValue = 'breakdown';
            }
            else if (this.context.valueMetricIndex != null)
                currentValue = `metric|${this.context.valueMetricIndex}`;
            this.$columnSelect.val(currentValue);

            appn.select.initForDesign({
                elements: this.$columnSelect,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        private initValueTypeSelects(): void {
            appn.select.initForDesign({
                elements: this.$root.find('.valueTypeSelect'),
                isIconButtonSelect: true,
                omitWidth: true
            });

            this.$root.find('.valueTypeSelect').on('select2:select', (e: any) => {
                const newType = e.params.data.id as FilterValueType;
                if (newType !== this.valueType) {
                    this.valueType = newType;
                    this.renderUI();
                }
            });
        }

        private openFormulaBuilder(): void {
            const currentValue = this.context.dynamicValue;

            appn.ui.formulabuilder.controlName = 'FilterValue';
            appn.ui.formulabuilder.$input = this.$dynamicInput;
            appn.ui.formulabuilder.currentFormula = currentValue || '';
            appn.ui.formulabuilder.currentMode = appn.designerv2?.FormulaType?.normal;
            appn.ui.formulabuilder.isFormula = true;
            appn.ui.formulabuilder.formulaUpdated = (formula: string) => {
                this.$dynamicInput.val(formula).trigger('input');
            };
            appn.ui.formulabuilder.showDialog();
        }

        private bindEvents(): void {
            this.$root.on('input change', '.filterValueText, .filterValueNumeric', (e) => {
                const target = $(e.currentTarget);
                let rawValue = target.val() as string;
                let finalValue: string | undefined;

                if (!rawValue || rawValue === '')
                    finalValue = undefined;
                else
                    finalValue = rawValue;

                this.context.value = finalValue;
                this.onValueChanged({ value: finalValue });
            });

            this.$root.on('change', '.filterDynamicValue', (e) => {
                const val = $(e.target).val() as string;
                this.context.dynamicValue = val || undefined;
                this.onValueChanged({ dynamicValue: val || undefined });
            });

            this.$root.on('click', '.buildDynamicIcon', (e) => {
                e.preventDefault();
                this.openFormulaBuilder();
            });

            this.$root.on('change', '.filterValueColumnSelect', (e) => {
                const val = $(e.target).val() as string;
                const aggregation = this.context.aggregation;

                if (val) {
                    if (val === 'group' && aggregation?.groupBy) {
                        this.context.valueColumn = { datasetIndex: aggregation.groupBy.datasetIndex, columnId: aggregation.groupBy.columnId };
                        this.context.valueMetricIndex = undefined;
                        this.onValueChanged({ valueColumn: this.context.valueColumn, valueMetricIndex: undefined });
                    } else if (val === 'breakdown' && aggregation?.breakDownBy) {
                        this.context.valueColumn = { datasetIndex: aggregation.breakDownBy.datasetIndex, columnId: aggregation.breakDownBy.columnId };
                        this.context.valueMetricIndex = undefined;
                        this.onValueChanged({ valueColumn: this.context.valueColumn, valueMetricIndex: undefined });
                    } else if (val.startsWith('metric|')) {
                        const index = parseInt(val.split('|')[1], 10);
                        this.context.valueColumn = undefined;
                        this.context.valueMetricIndex = index;
                        this.onValueChanged({ valueColumn: undefined, valueMetricIndex: index });
                    }
                } else {
                    this.context.valueColumn = undefined;
                    this.context.valueMetricIndex = undefined;
                    this.onValueChanged({ valueColumn: undefined, valueMetricIndex: undefined });
                }
            });

            this.$root.on('change', '.filterGlobalFilterSelect', (e) => {
                const val = $(e.target).val() as string;
                this.context.globalFilterId = val || undefined;
                this.onValueChanged({ globalFilterId: val || undefined });
            });
        }

        private populateGlobalFilterSelect(): void {
            // HAVING criteria only take values from number-range filters.
            const compatible = getGlobalFiltersForValueType(this.context.operator, true);
            const current = this.context.globalFilterId || '';

            this.$globalFilterSelect.empty().append(`<option value="">${T('Choose global filter')}...</option>`);
            compatible.forEach(f => this.$globalFilterSelect.append($('<option>').val(f.id).text(f.label || f.id)));

            appn.select.initForDesign({
                elements: this.$globalFilterSelect.not('.select2-hidden-accessible'),
                omitWidth: true,
                minimumResultsForSearch: '10'
            });

            this.$globalFilterSelect.val(compatible.some(f => f.id === current) ? current : '')
                .trigger('change.select2');
        }

        public destroy(): void {
            this.$root.remove();
        }
    }

    class AggregationFilterCriterionEditor {
        public static readonly operatorRequirements = FilterCriterionEditor.operatorRequirements;
        private $filterCriterionRow: JQuery;
        private $removeCriterion: JQuery;
        private $column: JQuery;
        private $operator: JQuery;
        private $valueArea: JQuery;
        private $valuePlaceholder: JQuery;
        private $value2Row: JQuery;
        private $value1Container: JQuery;
        private $valueSeparator: JQuery;
        private $value2Container: JQuery;
        private $allOperatorOptions: JQuery;

        private valueEditor1: AggregationFilterCriterionValueEditor | null = null;
        private valueEditor2: AggregationFilterCriterionValueEditor | null = null;

        private criterion: IDataQueryAggregationFilter;
        private aggregation: IDataQueryAggregation | null;
        private datasetRefs: IDatasetReference[];
        private availableColumns: { id: string; name: string; type: string, source: 'group' | 'breakdown' | 'metric' }[] = [];
        private currentColumnType: string | null = null;

        private onChanged: (newData: IDataQueryAggregationFilter) => void;
        private onDelete: () => void;

        constructor(container: JQuery, criterion: IDataQueryAggregationFilter, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], onChanged: (d: IDataQueryAggregationFilter) => void, onDelete: () => void) {
            this.$filterCriterionRow = appn.utils.cloneTemplate('editor-filterCriterionRow');
            container.append(this.$filterCriterionRow);

            this.criterion = criterion;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;
            this.onChanged = onChanged;
            this.onDelete = onDelete;

            this.$column = this.$filterCriterionRow.find('.filterColumnSelect');
            this.$operator = this.$filterCriterionRow.find('.filterOperatorSelect');
            this.$removeCriterion = this.$filterCriterionRow.find('.removeBtn');

            this.$allOperatorOptions = this.$operator.find('option').clone();
            
            this.$valueArea = this.$filterCriterionRow.find('.value-input-area');
            this.$valuePlaceholder = this.$valueArea.find('.filterValuePlaceholder');
            this.$value1Container = this.$valueArea.find('.value1-input-container');
            this.$value2Row = this.$filterCriterionRow.find('.value2-row');
            this.$valueSeparator = this.$value2Row.find('.value-separator');
            this.$value2Container = this.$value2Row.find('.value2-input-container');

            this.update(criterion, aggregation, datasetRefs);
            this.bindEvents();
        }

        public update(criterion: IDataQueryAggregationFilter, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], onChanged?: (d: IDataQueryAggregationFilter) => void): void {
            if (onChanged)
                this.onChanged = onChanged;
            this.criterion = criterion;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;

            const selectedValue = (appn.utils.hasValue(this.criterion.metricIndex) ? `metric|${this.criterion.metricIndex}` : null)
                || (this.criterion.column && appn.utils.hasValue(this.criterion.column.datasetIndex) ? `col|${this.criterion.column.datasetIndex}|${this.criterion.column.columnId}` : '');

            this.populateColumns();
            this.$column.val(selectedValue);
            this.updateCurrentColumnType(selectedValue);
            this.populateOperators();
            this.$operator.val(this.criterion.operator || 'Equals');
            this.updateValueInputsVisibilityAndValues();
            this.$filterCriterionRow.toggleClass('filter-criterion-global', !!this.criterion.globalFilterId);
        }

        // showDeleteButton DOES HAVE USAGES via AggregationFilterGroupEditor children which can be AggregationFilterGroupEditor | AggregationFilterCriterionEditor
        public showDeleteButton(visible: boolean): void {
            this.$removeCriterion.toggle(visible);
        }

        private populateColumns(): void {
            const datasets = designer.getDatasets();
            this.availableColumns = [];
            this.$column.empty().append($('<option>').val('').text('Choose column...'));

            if (!this.aggregation)
                return;

            const { groupBy, breakDownBy, metrics } = this.aggregation;
            const groups: { label: string, value: string, type: string }[] = [];
            const showPrefix = this.datasetRefs.length > 1;
            const getGroupLabelAndMeta = (colIdentifier: IDataQueryColumnIdentifier | undefined, source: 'group' | 'breakdown'): { label: string, value: string, type: string } | null => {
                if (!colIdentifier)
                    return null;

                const { datasetRef, dataset, columnMeta } = getContextualColumnMeta(colIdentifier.datasetIndex, colIdentifier.columnId, this.datasetRefs, datasets);
                if (!datasetRef || !dataset || !columnMeta)
                    return null;

                const datasetPrefix = datasetRef.alias || dataset.name;
                const columnText = getColumnOptionText(columnMeta);
                const label = showPrefix ? `${datasetPrefix}.${columnText}` : columnText;
                const value = `col|${colIdentifier.datasetIndex}|${colIdentifier.columnId}`;

                this.availableColumns.push({ id: value, name: label, type: columnMeta.type, source: source });
                
                return { label, value, type: columnMeta.type };
            };

            const groupByMeta = getGroupLabelAndMeta(groupBy, 'group');
            if (groupByMeta)
                groups.push(groupByMeta);

            const breakDownByMeta = getGroupLabelAndMeta(breakDownBy, 'breakdown');
            if (breakDownByMeta)
                groups.push(breakDownByMeta);

            if (groups.length > 0) {
                const $optgroup = $('<optgroup label="Groups"></optgroup>');
                groups.forEach(g => $optgroup.append($('<option>').val(g.value).text(g.label)));
                
                this.$column.append($optgroup);
            }

            if (metrics && metrics.length > 0) {
                const $optgroup = $('<optgroup label="Metrics"></optgroup>');
                metrics.forEach((m, index) => {
                    const value = `metric|${index}`;
                    const text = getMetricDisplayText(m, index, this.aggregation, this.datasetRefs, datasets);

                    $optgroup.append($('<option>').val(value).text(text));
                    
                    this.availableColumns.push({ id: value, name: text, type: 'Number', source: 'metric' });
                });
                
                this.$column.append($optgroup);
            }
        }

        private updateCurrentColumnType(selectedValue: string): void {
            if (!selectedValue) {
                this.currentColumnType = null;
                return;
            }

            const col = this.availableColumns.find(c => c.id === selectedValue);
            this.currentColumnType = col ? col.type : null;
        }

        private populateOperators(): void {
            const currentOperator = this.$operator.val() as string;
            this.$operator.empty();

            // Populate only operators valid for the current column type
            const operators = this.$allOperatorOptions.clone();
            operators.each((_, opt) => {
                const $opt = $(opt);
                const opValue = $opt.val() as string;
                const req = AggregationFilterCriterionEditor.operatorRequirements[opValue];
                if (this.currentColumnType && req && req.types.includes(this.currentColumnType))
                    this.$operator.append($opt);
                else if (!this.currentColumnType)
                    this.$operator.append($opt);
            });
            if (this.$operator.find(`option[value="${currentOperator}"]`).length > 0)
                this.$operator.val(currentOperator);
            else
                this.$operator.val('Equals');
        }

        private updateValueInputsVisibilityAndValues(): void {
            const operator = this.$operator.val() as string;
            const req = AggregationFilterCriterionEditor.operatorRequirements[operator];
            // A global filter criterion's bounds both come from the filter — a single picker replaces both slots.
            const valuesNeeded = this.criterion.globalFilterId ? 1 : (req ? req.values : 0);

            this.$valuePlaceholder.hide();
            this.$value2Row.hide();
            this.$valueSeparator.hide();

            const createContextForValue1 = (): IAggregationFilterCriterionValueEditorContext => ({
                currentColumnType: this.currentColumnType,
                aggregation: this.aggregation,
                datasetRefs: this.datasetRefs,
                value: this.criterion.value,
                dynamicValue: this.criterion.dynamicValue,
                valueColumn: this.criterion.valueColumn,
                valueMetricIndex: this.criterion.valueMetricIndex,
                operator: this.criterion.operator,
                globalFilterId: this.criterion.globalFilterId
            });

            const createContextForValue2 = (): IAggregationFilterCriterionValueEditorContext => ({
                currentColumnType: this.currentColumnType,
                aggregation: this.aggregation,
                datasetRefs: this.datasetRefs,
                value: this.criterion.value2,
                dynamicValue: this.criterion.dynamicValue2,
                valueColumn: this.criterion.value2Column,
                valueMetricIndex: this.criterion.value2MetricIndex,
                operator: this.criterion.operator,
                globalFilterId: this.criterion.globalFilterId
            });

            if (valuesNeeded === 0) {
                this.$valuePlaceholder.show();
                if (this.valueEditor1)
                    this.valueEditor1.hide();
                if (this.valueEditor2)
                    this.valueEditor2.hide();
            } else if (valuesNeeded >= 1) {
                if (!this.valueEditor1) {
                    this.valueEditor1 = new AggregationFilterCriterionValueEditor(
                        this.$value1Container,
                        (updates) => {
                            this.criterion = { 
                                ...this.criterion, 
                                value: updates.value,
                                dynamicValue: updates.dynamicValue,
                                valueColumn: updates.valueColumn,
                                valueMetricIndex: updates.valueMetricIndex,
                                globalFilterId: updates.globalFilterId
                            };
                            this.onChanged(this.criterion);
                        }
                    );
                }
                this.valueEditor1.update(createContextForValue1());
                this.valueEditor1.show();
            }

            if (valuesNeeded === 2) {
                this.$value2Row.show();
                this.$valueSeparator.show();

                if (!this.valueEditor2) {
                    this.valueEditor2 = new AggregationFilterCriterionValueEditor(
                        this.$value2Container,
                        (updates) => {
                            this.criterion = { 
                                ...this.criterion, 
                                value2: updates.value,
                                dynamicValue2: updates.dynamicValue,
                                value2Column: updates.valueColumn,
                                value2MetricIndex: updates.valueMetricIndex
                            };
                            this.onChanged(this.criterion);
                        }
                    );
                }
                this.valueEditor2.update(createContextForValue2());
                this.valueEditor2.show();
            }
        }

        private bindEvents(): void {
            this.$filterCriterionRow.on('click', '.removeBtn', () => this.onDelete());
            this.$filterCriterionRow.on('change', '.filterColumnSelect', e => {
                this.criterion = { ...this.criterion, column: undefined, metricIndex: undefined };

                const val = $(e.target).val() as string;
                if (val) {
                    const parts = val.split('|');
                    if (parts[0] === 'metric')
                        this.criterion.metricIndex = parseInt(parts[1], 10);
                    else if (parts[0] === 'col')
                        this.criterion.column = { datasetIndex: parseInt(parts[1], 10), columnId: parts[2] };
                }

                this.updateCurrentColumnType(val);
                this.populateOperators();
                
                this.criterion.operator = this.$operator.val() as string;
                this.criterion.value = undefined;
                this.criterion.value2 = undefined;
                this.criterion.dynamicValue = undefined;
                this.criterion.dynamicValue2 = undefined;
                this.criterion.valueColumn = undefined;
                this.criterion.valueMetricIndex = undefined;
                this.criterion.value2Column = undefined;
                this.criterion.value2MetricIndex = undefined;
                
                this.onChanged(this.criterion);
            });
            
            this.$filterCriterionRow.on('change', '.filterOperatorSelect', e => {
                const newOperator = $(e.target).val() as string;

                // HAVING criteria only take number-range filters — an operator that can't consume one drops the reference.
                if (this.criterion.globalFilterId && !(GLOBAL_FILTER_TYPES_BY_OPERATOR[newOperator] || []).includes(DashboardGlobalFilterType.NumberRange))
                    this.criterion = { ...this.criterion, globalFilterId: undefined };

                this.criterion = { ...this.criterion, operator: newOperator };
                const req = AggregationFilterCriterionEditor.operatorRequirements[newOperator];
                const valuesNeeded = req ? req.values : 0;
                if (valuesNeeded < 1) {
                    this.criterion.value = undefined;
                    this.criterion.dynamicValue = undefined;
                    this.criterion.valueColumn = undefined;
                    this.criterion.valueMetricIndex = undefined;
                }
                if (valuesNeeded < 2) {
                    this.criterion.value2 = undefined;
                    this.criterion.dynamicValue2 = undefined;
                    this.criterion.value2Column = undefined;
                    this.criterion.value2MetricIndex = undefined;
                }
                
                this.onChanged(this.criterion);
            });
        }

        public destroy(): void {
            this.valueEditor1?.destroy();
            this.valueEditor2?.destroy();
            this.$filterCriterionRow.remove();
        }
    }
    class AggregationFilterGroupEditor {
        private $filterGroup: JQuery;
        private $operation: JQuery;
        private $conditions: JQuery;
        private $removeGroup: JQuery;
        private operationSelector: string;
        private filterGroup: IDataQueryAggregationFilter;
        private aggregation: IDataQueryAggregation | null;
        private datasetRefs: IDatasetReference[];
        private childEditors: (AggregationFilterGroupEditor | AggregationFilterCriterionEditor)[] = [];
        private onChanged: (newData: IDataQueryAggregationFilter) => void;
        private onDelete: () => void;
        private isRoot: boolean;

        constructor(container: JQuery, filterGroup: IDataQueryAggregationFilter, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[],
                    onChanged: (d: IDataQueryAggregationFilter) => void, onDelete: () => void, isRoot = false) {

            this.$filterGroup = isRoot ?
                container : appn.utils.cloneTemplate('editor-filterGroup');
            if (!isRoot)
                container.append(this.$filterGroup);

            this.isRoot = isRoot;
            this.filterGroup = filterGroup;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;
            this.onChanged = onChanged;
            this.onDelete = onDelete;
            this.operationSelector = isRoot ? '.filterLogicalOperator' : '.groupLogicalOperator';
            this.$operation = this.$filterGroup.find(this.operationSelector);
            this.$conditions = this.$filterGroup.find('.filterConditionsList');
            this.$removeGroup = this.$filterGroup.find('.removeGroupBtn');

            this.update(filterGroup, aggregation, datasetRefs);
            this.bindEvents();
        }

        public update(filterGroup: IDataQueryAggregationFilter, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], onChanged?: (d: IDataQueryAggregationFilter) => void, onDelete?: () => void): void {
            if (onChanged)
                this.onChanged = onChanged;
            if (onDelete)
                this.onDelete = onDelete;
            this.filterGroup = filterGroup;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;
            this.$operation.val(this.filterGroup.groupOperator || 'AND');

            const newConditions = this.filterGroup.groupConditions || [];
            while (this.childEditors.length > newConditions.length)
                this.childEditors.pop()?.destroy();

            const canDeleteChildren = this.isRoot || newConditions.length > 2;

            newConditions.forEach((condition, index) => {
                const onChildChanged = (newChildData: IDataQueryAggregationFilter) => {
                    this.filterGroup = appn.utils.deepClone(this.filterGroup);
                    this.filterGroup.groupConditions![index] = newChildData;
                    this.onChanged(this.filterGroup);
                };
                const onChildDeleted = () => {
                    this.filterGroup = appn.utils.deepClone(this.filterGroup);
                    this.filterGroup.groupConditions!.splice(index, 1);
                    if (this.isRoot && this.filterGroup.groupConditions!.length === 0)
                        this.onDelete();
                    else
                        this.onChanged(this.filterGroup);
                };

                const existingEditor = this.childEditors[index];
                const isGroup = !!condition.groupConditions;
                const typeMatches = existingEditor
                    && ((isGroup && existingEditor instanceof AggregationFilterGroupEditor)
                        || (!isGroup && existingEditor instanceof AggregationFilterCriterionEditor));

                if (typeMatches) {
                    (existingEditor as any).update(condition, this.aggregation, this.datasetRefs, onChildChanged, onChildDeleted);
                } else {
                    if (existingEditor)
                        existingEditor.destroy();

                    const $listItem = $('<li></li>');
                    this.$conditions.append($listItem);

                    if (isGroup) {
                        $listItem.addClass('mt-2');
                        this.childEditors[index] = new AggregationFilterGroupEditor($listItem, condition, this.aggregation, this.datasetRefs, onChildChanged, onChildDeleted);
                    } else
                        this.childEditors[index] = new AggregationFilterCriterionEditor($listItem, condition, this.aggregation, this.datasetRefs, onChildChanged, onChildDeleted);
                }
                const editor = this.childEditors[index];
                if (editor)
                    editor.showDeleteButton(canDeleteChildren);
            });
        }

        public showDeleteButton(visible: boolean): void {
            this.$removeGroup.toggle(visible);
        }

        private bindEvents(): void {
            const $removeEle = this.$filterGroup.parent().is('li') ? this.$filterGroup.parent() : this.$filterGroup;
            $removeEle.on('click', '.removeGroupBtn', (e) => {
                e.stopPropagation();
                this.onDelete();
            });
            this.$filterGroup.on('change', this.operationSelector, e => {
                this.filterGroup = { ...this.filterGroup, groupOperator: $(e.target).val() as any };
                this.onChanged(this.filterGroup);
            });
            this.$filterGroup.on('click', '.addCriterionToGroupBtn, .addFilterCriterion', (e) => {
                e.stopPropagation();
                this.addCondition(false);
            });
            this.$filterGroup.on('click', '.addGroupToGroupBtn, .addFilterGroup', (e)  => {
                e.stopPropagation();
                this.addCondition(true);
            });
        }

        private addCondition(isGroup: boolean): void {
            this.filterGroup = appn.utils.deepClone(this.filterGroup);
            if (!this.filterGroup.groupConditions)
                this.filterGroup.groupConditions = [];

            if (isGroup) {
                this.filterGroup.groupConditions.push({
                    groupOperator: 'OR',
                    groupConditions: [
                        { operator: 'Equals' },
                        { operator: 'Equals' }
                    ]
                });
            } else {
                this.filterGroup.groupConditions.push({ operator: 'Equals' });
            }
            this.onChanged(this.filterGroup);
        }

        public destroy(): void {
            this.childEditors.forEach(c => c.destroy());
            this.childEditors = [];
            if (!this.$filterGroup.parent().is('li'))
                this.$filterGroup.remove();
            else
                this.$filterGroup.parent().remove();
        }
    }
    class AggregationFiltersEditor extends DataQueryEditorBase {
        private $multiCriteriaHint!: JQuery;
        private rootGroupEditor: AggregationFilterGroupEditor | null = null;

        init() {
            this.$multiCriteriaHint = this.$editor.find('.filterMultiCriteriaHint');
        }

        update(): void {
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const aggregation = dataQuery?.aggregation;
            const originalFilters = this.getValue() as IDataQueryAggregationFilter | null;
            const filters = originalFilters || { groupOperator: 'AND', groupConditions: [] };

            if (this.rootGroupEditor) {
                this.rootGroupEditor.update(filters, aggregation, datasetRefs);
            } else {
                const isRoot = true;
                const onChange = (newFilters: IDataQueryAggregationFilter) => this.setValue(newFilters);
                const onDelete = () => this.setValue(null);
                this.rootGroupEditor = new AggregationFilterGroupEditor(this.$editor, filters, aggregation, datasetRefs, onChange, onDelete, isRoot);
            }

            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                omitWidth: true,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });

            if (this.$multiCriteriaHint.length > 0) {
                const criteriaCount = filters.groupConditions?.length || 0;
                this.$multiCriteriaHint.toggle(criteriaCount >= 2);
            }
        }

        bindEvents(): void {
            this.subscribeToEvent(DASH_EVENTS.ALIAS_CHANGED, (payload) => {
                if (payload.componentId === this.component.id)
                    this.update();
            });

            this.subscribeToEvent(DASH_EVENTS.METRIC_REMOVED, (payload: { componentId: string, removedIndex: number }) => {
                if (payload.componentId !== this.component.id) return;

                const rootFilter = this.getValueForEdit<IDataQueryAggregationFilter>();
                if (!rootFilter) return;

                const removedIndex = payload.removedIndex;
                let wasChanged = false;

                const updateIndicesRecurse = (filter: IDataQueryAggregationFilter): void => {
                    if (filter.groupConditions) {
                        filter.groupConditions.forEach(updateIndicesRecurse);
                    } else if (appn.utils.hasValue(filter.metricIndex)) {
                        const refIndex = filter.metricIndex!;
                        if (refIndex === removedIndex) {
                            filter.metricIndex = undefined;
                            wasChanged = true;
                        } else if (refIndex > removedIndex) {
                            filter.metricIndex!--;
                            wasChanged = true;
                        }
                    }
                };

                updateIndicesRecurse(rootFilter);

                if (wasChanged)
                    this.setValue(rootFilter);
            });
        }

        destroy(): void {
            this.rootGroupEditor?.destroy();
            this.rootGroupEditor = null;
            super.destroy();
        }
    }
    
    class ProjectionRowEditor {
        private $projectionRow: JQuery;
        private $column: JQuery;
        private $alias: JQuery;
        private projection: IDataQueryColumnIdentifier;
        private aliasedDatasets: IDatasetReference[];
        private onChanged: (newData: IDataQueryColumnIdentifier) => void;
        private onDelete: () => void;

        constructor(container: JQuery, projection: IDataQueryColumnIdentifier, datasetRefs: IDatasetReference[], onChanged: (d: IDataQueryColumnIdentifier) => void, onDelete: () => void) {
            this.$projectionRow = appn.utils.cloneTemplate('editor-projectionRow');
            container.append(this.$projectionRow);
            this.projection = projection;
            this.aliasedDatasets = datasetRefs;
            this.onChanged = onChanged;
            this.onDelete = onDelete;
            this.$column = this.$projectionRow.find('.projection-column-select');
            this.$alias = this.$projectionRow.find('.projection-alias-input');
            this.update(projection, datasetRefs);
            this.bindEvents();
        }

        update(projection: IDataQueryColumnIdentifier, datasetRefs: IDatasetReference[]): void {
            this.projection = projection;
            this.aliasedDatasets = datasetRefs;
            
            const selectedValue = appn.utils.hasValue(this.projection.datasetIndex) && this.projection.columnId ? `${this.projection.datasetIndex}|${this.projection.columnId}` : '';

            populatePrefixedColumnSelect(this.$column, this.aliasedDatasets, null, selectedValue);

            this.$alias.val(this.projection.alias || '');
        }

        bindEvents(): void {
            this.$projectionRow.on('click', '.remove-projection', () => this.onDelete());
            this.$projectionRow.on('change', '.projection-column-select', e => {
                const val = $(e.target).val() as string;
                if (val) {
                    const [datasetIndexStr, columnId] = val.split('|');
                    this.onChanged({ ...this.projection, datasetIndex: parseInt(datasetIndexStr, 10), columnId });
                }
            });
            this.$projectionRow.on('input', '.projection-alias-input', e => {
                this.projection.alias = $(e.target).val() as string;
                this.onChanged(this.projection);
            });
            this.$projectionRow.on('change', '.projection-alias-input', e => {
                this.projection.alias = $(e.target).val() as string;
                this.onChanged(this.projection);
            });
        }

        destroy(): void {
            this.$projectionRow.remove();
        }
    }
    class ProjectionSelectorEditor extends DataQueryEditorBase {
        protected $projectionsList!: JQuery;
        private projectionEditors: ProjectionRowEditor[] = [];

        init() {
            this.$projectionsList = this.$editor.find('.projections-list');
        }

        update(): void {
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const projections = (this.getValue() as IDataQueryProjection)?.columns || [];
            while (this.projectionEditors.length > projections.length)
                this.projectionEditors.pop()?.destroy();
            projections.forEach((proj, index) => {
                if (this.projectionEditors.length <= index) {
                    const onChange = (newProjection: IDataQueryColumnIdentifier) => {
                        const currentProjections: IDataQueryProjection = this.getValueForEdit<IDataQueryProjection>() || { columns: [] };
                        currentProjections.columns[index] = newProjection;
                        this.setValue(currentProjections);
                    };
                    const onDelete = () => {
                        const currentProjection: IDataQueryProjection = this.getValueForEdit<IDataQueryProjection>() || { columns: [] };
                        currentProjection.columns.splice(index, 1);
                        this.setValue(currentProjection.columns.length === 0 ? null : currentProjection);
                    }
                    const editor = new ProjectionRowEditor(this.$projectionsList, proj, datasetRefs, onChange, onDelete);
                    this.projectionEditors.push(editor);
                } else
                    this.projectionEditors[index].update(proj, datasetRefs);
            });
            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.$editor.on('click', '.add-projection', () => {
                const projections = this.getValueForEdit<IDataQueryProjection>() || { columns: [] };
                projections.columns.push({ datasetIndex: 0, columnId: '' }); // Default to datasetIndex 0
                this.setValue(projections);
            });
        }

        destroy(): void {
            this.projectionEditors.forEach(e => e.destroy());
            this.projectionEditors = [];
            super.destroy();
        }
    }

    class MetricRowEditor {
        private $metricRow: JQuery;
        private $function: JQuery;
        private $column: JQuery;
        private $alias: JQuery;
        private $removeButton: JQuery
        private metric: IDataQueryMetric;
        private datasetRefs: IDatasetReference[];
        private isSingleMode: boolean;
        
        private onInput: (newData: IDataQueryMetric) => void;
        private onChanged: (newData: IDataQueryMetric) => void;
        private onDelete: () => void;

        constructor(container: JQuery, metric: IDataQueryMetric, datasetRefs: IDatasetReference[], onInput: (d: IDataQueryMetric) => void, onChanged: (d: IDataQueryMetric) => void, onDelete: () => void, isSingleMode: boolean) {
            this.$metricRow = appn.utils.cloneTemplate('editor-metricRow');
            container.append(this.$metricRow);

            this.metric = metric;
            this.datasetRefs = datasetRefs;
            this.onInput = onInput;
            this.onChanged = onChanged;
            this.onDelete = onDelete;
            this.isSingleMode = isSingleMode;
            this.$function = this.$metricRow.find('.metric-function');
            this.$column = this.$metricRow.find('.metric-column');
            this.$alias = this.$metricRow.find('.metric-alias');
            this.$removeButton = this.$metricRow.find('.remove-metric');

            this.update(metric, datasetRefs);
            this.bindEvents();
        }

        private _updateColumnVisibility(): void {
            const func = this.$function.val() as string;
            const $select = this.$metricRow.find('.metric-column-wrapper');
            const $placeholder = this.$metricRow.find('.metric-column-placeholder');

            if (func === 'Count') {
                $select.hide();
                $placeholder.show();
            } else {
                $select.show();
                $placeholder.hide();
            }
        }

        private _getColumnFilter(): string[] | null {
            const func = this.$function.val() as string;
            switch (func) {
                case 'Avg':
                case 'Sum':
                    return ['Number'];
                case 'CountDistinct':
                case 'Min':
                case 'Max':
                    return null; // All types supported
                case 'Count':
                default:
                    return null; // Not applicable or all types
            }
        }

        update(metric: IDataQueryMetric, datasetRefs: IDatasetReference[], metricsCount?: number): void {
            this.metric = metric;
            this.datasetRefs = datasetRefs;
            this.$function.val(this.metric.function);
            this.$alias.val(this.metric.alias);
            this.$alias.attr('placeholder', 'Alias (Optional)');

            const selectedValue = appn.utils.hasValue(this.metric.datasetIndex) && this.metric.columnId ? `${this.metric.datasetIndex}|${this.metric.columnId}` : '';
            const columnTypeFilter = this._getColumnFilter();
            
            populatePrefixedColumnSelect(this.$column, this.datasetRefs, columnTypeFilter, selectedValue);
            
            this._updateColumnVisibility();

            const canDelete = !this.isSingleMode || (metricsCount && metricsCount > 1);

            this.$removeButton.toggle(canDelete);
        }

        bindEvents(): void {
            this.$metricRow.on('click', '.remove-metric', () => this.onDelete());
            this.$metricRow.on('change', '.metric-function', e => {
                const newFunc = $(e.target).val() as any;
                this._updateColumnVisibility();

                const newAlias = this.$alias.val() as string;
                if (newFunc === 'Count')
                    // Default Count to primary dataset (index 0), clear column
                    this.onChanged({ ...this.metric, function: newFunc, alias: newAlias, datasetIndex: 0, columnId: undefined });
                else
                    this.onChanged({ ...this.metric, function: newFunc, alias: newAlias });
            });

            this.$metricRow.on('change', '.metric-column', e => {
                const val = $(e.target).val() as string;
                let newData = { 
                    ...this.metric, 
                    alias: this.$alias.val() as string
                };
                if (val) {
                    const [datasetIndexStr, columnId] = val.split('|');
                    newData.datasetIndex = parseInt(datasetIndexStr, 10);
                    newData.columnId = columnId;
                } else {
                    newData.datasetIndex = undefined;
                    newData.columnId = '';
                }

                this.onChanged(newData);
            });

            this.$alias.on('focus', e => $(e.target).data('old-alias', $(e.target).val() as string));
            this.$alias.on('input', e => {
                this.metric.alias = $(e.target).val() as string;
                // Call onInput to immediately update the state without de-duping
                this.onInput(this.metric);
            });
            this.$alias.on('change', e => {
                this.metric.alias = $(e.target).val() as string;

                // Trigger the parent's onChanged, which will run de-duplication
                this.onChanged(this.metric);
            });
        }

        destroy(): void {
            this.$metricRow.remove();
        }
    }
    class MetricsEditor extends DataQueryEditorBase {
        protected $metricsList!: JQuery;
        protected $addMetric!: JQuery;
        protected metricEditors: MetricRowEditor[] = [];

        init() {
            this.$metricsList = this.$editor.find('.metrics-list');
            this.$addMetric = this.$editor.find('.add-metric');
        }

        update(): void {
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const metrics = this.getValue() as IDataQueryMetric[] || [];
            const singleMode = this.propDef.config?.single ?? false;
            // single mode must always has one metric
            if (dataQuery && singleMode) {
                if (metrics.length === 0 && this.isEditorVisible()) {
                    const currentMetrics = this.getValueForEdit<IDataQueryMetric[]>() || [];
                    if (currentMetrics.length === 0) {
                        currentMetrics.push({
                            function: 'Count',
                            datasetIndex: 0,
                            columnId: undefined,
                            alias: ``
                        });
    
                        this.setValue(currentMetrics);
                        return;
                    }
                } else if (metrics.length == 1 && !this.isEditorVisible()) {
                    this.setValue([]);
                    return;
                }
            }

            while (this.metricEditors.length > metrics.length)
                this.metricEditors.pop()?.destroy();
            metrics.forEach((metric, index) => {
                if (this.metricEditors.length <= index) {
                    const onInput = (newMetricData: IDataQueryMetric) => {
                        const currentMetrics = this.getValueForEdit<IDataQueryMetric[]>() || [];
                        const oldAlias = currentMetrics[index].alias;
                        currentMetrics[index] = newMetricData;

                        this.setValue(currentMetrics); // Set value without de-dupe
                        if (oldAlias !== newMetricData.alias) {
                            eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                                componentId: this.component.id,
                                oldAlias: oldAlias,
                                newAlias: newMetricData.alias
                            });
                        }
                    };

                    const onChange = (newMetricData: IDataQueryMetric) => {
                        // This is the blur event, de-dupe aliases here.
                        const rootDataQuery = this.getDataQuery();
                        const currentMetrics = this.getValueForEdit<IDataQueryMetric[]>() || [];
                        const oldAlias = currentMetrics[index].alias;

                        // Pass the source type ('metric') and the metric's 'index'
                        newMetricData.alias = runAggregationAliasDeduplication(rootDataQuery, newMetricData.alias, 'metric', index);

                        currentMetrics[index] = newMetricData;
                        this.setValue(currentMetrics);
                        
                        if (oldAlias !== newMetricData.alias) {
                            eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                                componentId: this.component.id,
                                oldAlias: oldAlias,
                                newAlias: newMetricData.alias
                            });
                        }
                    }

                    const onDelete = () => {
                        const currentMetrics = this.getValueForEdit<IDataQueryMetric[]>() || [];
                        currentMetrics.splice(index, 1);
                        this.setValue(currentMetrics);
                        eventBus.publish(DASH_EVENTS.METRIC_REMOVED, { componentId: this.component.id, removedIndex: index });
                    };
                    const editor = new MetricRowEditor(this.$metricsList, metric, datasetRefs, onInput, onChange, onDelete, singleMode);
                    this.metricEditors.push(editor);
                } else
                    this.metricEditors[index].update(metric, datasetRefs, metrics.length);
            });

            // Hide the "Add Metric" button if in single mode, and we already have a metric
            this.$addMetric.toggle(!singleMode || metrics.length === 0);
            
            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                omitWidth: true,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.subscribeToEvent(DASH_EVENTS.ALIAS_CHANGED, (payload: { componentId: string, oldAlias: string, newAlias: string }) => {
                if (payload.componentId === this.component.id)
                    this.update();
            });

            this.$editor.on('click', '.add-metric', () => {
                const metrics = this.getValueForEdit<IDataQueryMetric[]>() || [];
                metrics.push({
                    function: 'Count',
                    datasetIndex: 0,
                    columnId: undefined,
                    alias: ''
                });
                this.setValue(metrics);
            });
        }

        destroy(): void {
            this.metricEditors.forEach(e => e.destroy());
            this.metricEditors = [];
            super.destroy();
        }
    }

    class ColumnSelectorEditor extends DataQueryEditorBase {
        protected $column!: JQuery;
        protected $aliasInput!: JQuery;
        protected $aliasContainer!: JQuery;
        protected $timeGrainSelect!: JQuery;
        protected $timeGrainContainer!: JQuery;
        private allowAlias!: boolean;
        private datasets!: IDatasetMeta[];

        init() {
            this.$column = this.$editor.find('.column-selector');
            this.$aliasContainer = this.$editor.find('.alias-container');
            this.$aliasInput = this.$editor.find('.alias-input');
            this.$timeGrainContainer = this.$editor.find('.time-grain-container');
            this.$timeGrainSelect = this.$editor.find('.time-grain-select');
            this.allowAlias = this.propDef.config?.allowAlias ?? false;
            this.datasets = designer.getDatasets();
        }

        update(): void {
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const currentValue = this.getValue() as IDataQueryColumnIdentifier;
            const selectedValue = appn.utils.hasValue(currentValue?.datasetIndex) && currentValue?.columnId ? `${currentValue.datasetIndex}|${currentValue.columnId}` : '';

            const allowedTypes = (this.propDef.config && this.propDef.config.allowedTypes)
                ? this.propDef.config.allowedTypes
                : null;

            populatePrefixedColumnSelect(this.$column, datasetRefs, allowedTypes, selectedValue);

            this.$aliasContainer.toggle(this.allowAlias && !!currentValue?.columnId);
            this.$aliasInput.val(currentValue?.alias || '');
            this.$aliasInput.attr('placeholder', 'Alias (Optional)');

            let showTimeGrain = false;
            if (currentValue?.columnId && appn.utils.hasValue(currentValue.datasetIndex)) {
                const { columnMeta } = getContextualColumnMeta(currentValue.datasetIndex, currentValue.columnId, datasetRefs, this.datasets);
                if (columnMeta && (columnMeta.type === 'Date'))
                    showTimeGrain = true;
                
                if (appn.utils.hasValue(currentValue?.timeGrain) && !showTimeGrain) {
                    this.setTimeGrain(undefined);
                    return;
                }
                
                if (showTimeGrain && !appn.utils.hasValue(currentValue?.timeGrain)){
                    this.setTimeGrain(InsightTimeGrain.Week);
                    return;
                }
            }
            this.$timeGrainContainer.toggle(showTimeGrain);
            this.$timeGrainSelect.val(currentValue?.timeGrain || appn.emptyString).trigger('change.select2');

            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }
        
        setTimeGrain(timeGrain: InsightTimeGrain | undefined): void {
            const colIdentifier: IDataQueryColumnIdentifier = this.getValueForEdit();
            if (!colIdentifier)
                return;

            colIdentifier.timeGrain = timeGrain;
            this.setValue(colIdentifier);
        }

        bindEvents(): void {
            this.subscribeToEvent(DASH_EVENTS.ALIAS_CHANGED, (payload: { componentId: string, oldAlias: string, newAlias: string }) => {
                if (payload.componentId === this.component.id)
                    this.update();
            });

            this.$editor.on('change', '.column-selector', (e) => {
                const val = $(e.target).val() as string;
                if (val) {
                    const colIdentifier: IDataQueryColumnIdentifier = this.getValueForEdit() || {};
                    const [datasetIndexStr, columnId] = val.split('|');
                    const datasetIndex = parseInt(datasetIndexStr, 10);

                    colIdentifier.datasetIndex = datasetIndex;
                    colIdentifier.columnId = columnId;

                    const dataQuery = this.getDataQuery();
                    const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
                    const { columnMeta } = getContextualColumnMeta(datasetIndex, columnId, datasetRefs, this.datasets);

                    const isDate = columnMeta && (columnMeta.type === 'Date');
                    colIdentifier.timeGrain = isDate ? (colIdentifier?.timeGrain || InsightTimeGrain.Week) : undefined;

                    this.setValue(colIdentifier);
                } else
                    this.setValue(null);
            });

            this.$editor.on('change', '.time-grain-select', (e) => {
                this.setTimeGrain($(e.target).val() as InsightTimeGrain || undefined);
            });

            this.$editor.on('input', '.alias-input', (e) => {
                const currentValue = this.getValueForEdit<IDataQueryColumnIdentifier>();
                if (currentValue) {
                    const oldAlias = currentValue.alias;
                    const newAlias = $(e.target).val() as string;

                    currentValue.alias = newAlias;
                    this.setValue(currentValue);
                    
                    if (oldAlias !== newAlias) {
                        eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                            componentId: this.component.id,
                            oldAlias: oldAlias,
                            newAlias: newAlias

                        });
                    }
                }
            });

            this.$editor.on('change', '.alias-input', (e) => {
                const currentValue = this.getValueForEdit<IDataQueryColumnIdentifier>();
                if (!currentValue)
                    return;

                const oldAlias = currentValue.alias;
                const newAlias = $(e.target).val() as string;
                const rootDataQuery = this.getDataQuery();

                currentValue.alias = runAggregationAliasDeduplication(rootDataQuery, newAlias, this.propDef.path || '');

                this.setValue(currentValue);
                
                if (oldAlias !== currentValue.alias) {
                    eventBus.publish(DASH_EVENTS.ALIAS_CHANGED, {
                        componentId: this.component.id,
                        oldAlias: oldAlias,
                        newAlias: currentValue.alias
                    });
                }
            });
        }
    }

    class BreakDownByLimitEditor extends DataQueryEditorBase {
        protected $type!: JQuery;
        protected $value!: JQuery;
        protected $metric!: JQuery;
        protected $valueContainer!: JQuery;
        protected $metricContainer!: JQuery;

        init() {
            this.$valueContainer = this.$editor.find('.breakdown-limit-value-container');
            this.$metricContainer = this.$editor.find('.breakdown-limit-metric-container');
            this.$type = this.$editor.find('.breakdown-limit-type');
            this.$value = this.$editor.find('.breakdown-limit-value');
            this.$metric = this.$editor.find('.breakdown-limit-metric');
        }

        update(): void {
            const limit = this.getValue() as IBreakDownByLimit;
            const metrics = this.getDataQuery().aggregation?.metrics || [];   
            const dataQuery = this.getDataQuery();
            const aggregation = dataQuery?.aggregation;
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const datasets = designer.getDatasets();

            const currentType = limit?.type ?? 'None';
            const isNone = currentType === 'None';

            this.$type.val(currentType);
            this.$value.val(limit?.limit ?? '');
            this.$metric.empty().append('<option value="">Select metric...</option>');
            metrics.forEach((m, index) => {
                const text = getMetricDisplayText(m, index, aggregation, datasetRefs, datasets);
                this.$metric.append($('<option>').val(index).text(text));
            });
            this.$metric.val(limit?.metricIndex?.toString() ?? '');

            this.$valueContainer.toggle(!isNone);
            this.$metricContainer.toggle(!isNone);

            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.subscribeToEvent(DASH_EVENTS.ALIAS_CHANGED, (payload) => {
                if (payload.componentId === this.component.id)
                    this.update();
            });

            this.subscribeToEvent(DASH_EVENTS.METRIC_REMOVED, (payload: { componentId: string, removedIndex: number }) => {
                if (payload.componentId !== this.component.id) return;

                const limit = this.getValueForEdit<IBreakDownByLimit>();
                if (!limit || !appn.utils.hasValue(limit.metricIndex)) return;

                const refIndex = limit.metricIndex!;
                const removedIndex = payload.removedIndex;

                if (refIndex === removedIndex) {
                    limit.metricIndex = undefined;
                    this.setValue(limit);
                } else if (refIndex > removedIndex) {
                    limit.metricIndex!--;
                    this.setValue(limit);
                }
                // No change if refIndex < removedIndex
            });

            this.$editor.on('change input', '.breakdown-limit-type, .breakdown-limit-value, .breakdown-limit-metric', () => {
                const type = this.$type.val() as string;

                if (type === 'None') {
                    this.$valueContainer.hide();
                    this.$metricContainer.hide();
                    this.setValue(undefined);
                    return;
                }

                this.$valueContainer.show();
                this.$metricContainer.show();

                const newLimit: IBreakDownByLimit = {
                    type: type as IBreakDownByLimit['type'],
                    limit: undefined,
                    metricIndex: undefined
                };

                const valStr = this.$value.val() as string;
                newLimit.limit = valStr ? parseInt(valStr, 10) : undefined;

                const metricIndexStr = this.$metric.val() as string;
                newLimit.metricIndex = metricIndexStr ? parseInt(metricIndexStr, 10) : undefined;

                this.setValue(newLimit);
            });
        }
    }

    class SortOrderRowEditor {
        private $sortOrderRow: JQuery;
        private $column: JQuery;
        private $direction: JQuery;
        private sortOrder: IDataQuerySortOrder;
        private projection: IDataQueryProjection | null;
        private aggregation: IDataQueryAggregation | null;
        private datasetRefs: IDatasetReference[];
        private usedColumns: Set<string | undefined>;

        private onChanged: (newData: IDataQuerySortOrder) => void;
        private onDelete: () => void;
        constructor(container: JQuery, sortOrder: IDataQuerySortOrder, projection: IDataQueryProjection | null, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], usedColumns: Set<string | undefined>, onChanged: (d: IDataQuerySortOrder) => void, onDelete: () => void) {
            this.$sortOrderRow = appn.utils.cloneTemplate('editor-sortOrderRow');
            container.append(this.$sortOrderRow);

            this.sortOrder = sortOrder;
            this.projection = projection;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;
            this.usedColumns = usedColumns;
            
            this.onChanged = onChanged;
            this.onDelete = onDelete;

            this.$column = this.$sortOrderRow.find('.sort-column-select');
            this.$direction = this.$sortOrderRow.find('.sort-direction-select');

            this.update(sortOrder, projection, aggregation, datasetRefs, usedColumns);
            this.bindEvents();
        }

        update(sortOrder: IDataQuerySortOrder, projection: IDataQueryProjection | null, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], usedColumns: Set<string | undefined>): void {
            this.sortOrder = sortOrder;
            this.projection = projection;
            this.aggregation = aggregation;
            this.datasetRefs = datasetRefs;
            this.usedColumns = usedColumns;

            this.$direction.val(this.sortOrder.type);
            const selectedValue = (appn.utils.hasValue(this.sortOrder.metricIndex) ? `metric|${this.sortOrder.metricIndex}` : null)
                || (this.sortOrder.column && appn.utils.hasValue(this.sortOrder.column.datasetIndex) ? `col|${this.sortOrder.column.datasetIndex}|${this.sortOrder.column.columnId}` : '');

            this.populateSortableColumnSelect(this.$column, selectedValue);
        }

        bindEvents(): void {
            this.$sortOrderRow.on('click', '.remove-sort-order', () => this.onDelete());
            this.$sortOrderRow.on('change', '.sort-direction-select', e => this.onChanged({ ...this.sortOrder, type: $(e.target).val() as any }));
            this.$sortOrderRow.on('change', '.sort-column-select', e => {
                const newSortOrder: IDataQuerySortOrder = { type: this.sortOrder.type, column: undefined, metricIndex: undefined };
                
                const val = $(e.target).val() as string;
                if (!val) {
                    this.onChanged(newSortOrder);
                    return;
                }

                const parts = val.split('|');
                if (parts[0] === 'metric')
                    newSortOrder.metricIndex = parseInt(parts[1], 10);
                else if (parts[0] === 'col')
                    newSortOrder.column = { datasetIndex: parseInt(parts[1], 10), columnId: parts[2] };

                this.onChanged(newSortOrder);
            });
        }

        private populateSortableColumnSelect($select: JQuery, selectedValue?: string): void {
            $select.empty().append('<option value="">Choose...</option>');
            const currentValue = (appn.utils.hasValue(this.sortOrder.metricIndex) ? `metric|${this.sortOrder.metricIndex}` : null)
                || (this.sortOrder.column && appn.utils.hasValue(this.sortOrder.column.datasetIndex) ? `col|${this.sortOrder.column.datasetIndex}|${this.sortOrder.column.columnId}` : '');

            const datasets = designer.getDatasets();
            const createAndAppendOptgroup = (label: string, options: ({ value: string; text: string } | null)[]) => {
                const optgroup = $(`<optgroup label="${label}"></optgroup>`);
                let hasOptions = false;
                options.forEach(opt => {
                    if (opt && opt.value) {
                        if (opt.value === currentValue || !this.usedColumns.has(opt.value)) {
                            optgroup.append($('<option>').val(opt.value).text(opt.text));
                            hasOptions = true;
                        }
                    }
                });
                if (hasOptions)
                    $select.append(optgroup);
            };

            if (this.projection) {
                createAndAppendOptgroup("Columns", this.projection.columns.map(p => {
                    const { columnMeta: column } = getContextualColumnMeta(p.datasetIndex, p.columnId, this.datasetRefs, datasets);
                    if (!column)
                        return null;

                    const text = p.alias || getColumnOptionText(column!);
                    const value = `col|${p.datasetIndex}|${p.columnId}`;

                    return { value, text };
                }));
            } else if (this.aggregation) {
                const groups = [this.aggregation.groupBy, this.aggregation.breakDownBy].filter(g => g?.columnId);
                if (groups.length > 0) {
                    const showPrefix = this.datasetRefs.length > 1;
                    createAndAppendOptgroup("Groups", groups.map(g => {
                        const { datasetRef, dataset, columnMeta: column } = getContextualColumnMeta(g!.datasetIndex, g!.columnId, this.datasetRefs, datasets);
                        if (!datasetRef || !dataset || !column)
                            return null;

                        const datasetPrefix = datasetRef.alias || dataset.name;
                        const columnText = getColumnOptionText(column);
                        const text = showPrefix ? `${datasetPrefix}.${columnText}` : columnText;
                        const value = `col|${g!.datasetIndex}|${g!.columnId}`;

                        return { value: value, text: text };
                    }));
                }
                if (this.aggregation.metrics?.length > 0){
                    createAndAppendOptgroup("Metrics", this.aggregation.metrics.map((m, index) => {
                        const value = `metric|${index}`;
                        const text = getMetricDisplayText(m, index, this.aggregation, this.datasetRefs, datasets);

                        return { value, text };
                    }));
                }
            }

            if (selectedValue)
                $select.val(selectedValue);
        }

        destroy(): void {
            this.$sortOrderRow.remove();
        }
    }
    class SortOrderEditor extends DataQueryEditorBase {
        protected $sortOrdersList!: JQuery;
        protected $addSortOrder!: JQuery;
        private sortOrderEditors: SortOrderRowEditor[] = [];

        init() {
            this.$sortOrdersList = this.$editor.find('.sort-orders-list');
            this.$addSortOrder = this.$editor.find('.add-sort-order');
        }

        update(): void {
            const sortOrders = this.getValue() as IDataQuerySortOrder[] || [];
            const dataQuery = this.getDataQuery();
            const datasetRefs = getDatasetRefsInDataQuery(dataQuery);
            const usedColumns = new Set((sortOrders).map(so => (appn.utils.hasValue(so.metricIndex) ? `metric|${so.metricIndex}` : null)
                || (so.column && appn.utils.hasValue(so.column.datasetIndex) ? `col|${so.column.datasetIndex}|${so.column.columnId}` : undefined)));

            const getSortableOptions = () => {
                const options: string[] = [];
                if (dataQuery?.projection) {
                    dataQuery.projection.columns.forEach(p => {
                        const value = `col|${p.datasetIndex}|${p.columnId}`;
                        if (appn.utils.hasValue(p.datasetIndex) && p.columnId)
                            options.push(value);
                    });
                } else if (dataQuery?.aggregation) {
                    const groups = [dataQuery.aggregation.groupBy, dataQuery.aggregation.breakDownBy]
                        .filter(g => g?.columnId);

                    groups.forEach(g => {
                        if (appn.utils.hasValue(g!.datasetIndex) && g!.columnId)
                            options.push(`col|${g!.datasetIndex}|${g!.columnId}`);
                    });
                    dataQuery.aggregation.metrics?.forEach((m, index) => {
                        options.push(`metric|${index}`);
                    });
                }
                return options;
            };

            const allSortableOptions = getSortableOptions();
            this.$addSortOrder.toggle(allSortableOptions.length > sortOrders.length);

            while (this.sortOrderEditors.length > sortOrders.length)
                this.sortOrderEditors.pop()?.destroy();
            
            sortOrders.forEach((sortOrder, index) => {
                if (this.sortOrderEditors.length <= index) {
                    const onChange = (newSortData: IDataQuerySortOrder) => {
                        const currentSorts = this.getValueForEdit<IDataQuerySortOrder[]>() || [];
                        currentSorts[index] = newSortData;
                        this.setValue(currentSorts);
                    };
                    const onDelete = () => {
                        const currentSorts = this.getValueForEdit<IDataQuerySortOrder[]>() || [];
                        currentSorts.splice(index, 1);
                        this.setValue(currentSorts);
                    };
                    const editor = new SortOrderRowEditor(this.$sortOrdersList, sortOrder, dataQuery.projection, dataQuery.aggregation, datasetRefs, usedColumns, onChange, onDelete);
                    this.sortOrderEditors.push(editor);
                } else
                    this.sortOrderEditors[index].update(sortOrder, dataQuery.projection, dataQuery.aggregation, datasetRefs, usedColumns);
            });
            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                omitWidth: true,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.subscribeToEvent(DASH_EVENTS.ALIAS_CHANGED, (payload) => {
                if (payload.componentId === this.component.id)
                    this.update();
            });
            
            this.subscribeToEvent(DASH_EVENTS.METRIC_REMOVED, (payload: { componentId: string, removedIndex: number }) => {
                if (payload.componentId !== this.component.id) return;

                const sortOrders = this.getValueForEdit<IDataQuerySortOrder[]>() || [];
                if (sortOrders.length === 0) 
                    return;

                const removedIndex = payload.removedIndex;
                let wasChanged = false;

                sortOrders.forEach(so => {
                    const refIndex = so.metricIndex;
                    if (appn.utils.hasValue(refIndex)) {
                        if (refIndex === removedIndex) {
                            so.metricIndex = undefined;
                            wasChanged = true;
                        } else if (refIndex! > removedIndex) {
                            so.metricIndex!--;
                            wasChanged = true;
                        }
                    }
                });

                if (wasChanged)
                    this.setValue(sortOrders);
            });

            this.$editor.on('click', '.add-sort-order', () => {
                const sortOrders = this.getValueForEdit<IDataQuerySortOrder[]>() || [];
                sortOrders.push({ type: 'Asc' });
                this.setValue(sortOrders);
            });
        }

        destroy(): void {
            this.sortOrderEditors.forEach(e => e.destroy());
            this.sortOrderEditors = [];
            super.destroy();
        }
    }

    class ReferenceLineRowEditor {
        private $row: JQuery;
        private $label: JQuery;
        private $type: JQuery;
        private $value: JQuery;
        private $valuePlaceholder: JQuery;
        private $color: JQuery;
        private $style: JQuery;
        private $removeBtn: JQuery;

        private data: IReferenceLine;
        private onChange: (d: IReferenceLine) => void;
        private onDelete: () => void;

        constructor(container: JQuery, data: IReferenceLine, onChange: (d: IReferenceLine) => void, onDelete: () => void) {
            this.$row = appn.utils.cloneTemplate('editor-referenceLineRow');
            container.append(this.$row);

            this.data = data;
            this.onChange = onChange;
            this.onDelete = onDelete;

            this.$label = this.$row.find('.ref-label');
            this.$type = this.$row.find('.ref-type');
            this.$value = this.$row.find('.ref-value');
            this.$valuePlaceholder = this.$row.find('.ref-value-placeholder');
            this.$color = this.$row.find('.ref-color');
            this.$style = this.$row.find('.ref-style');
            this.$removeBtn = this.$row.find('.remove-ref');

            // Initialize 3rd party controls once
            appn.select.initForDesign({ elements: this.$row.find('select') });
            (this.$color as any).minicolors(new appn.minicolors.Options());

            this.update(data);
            this.bindEvents();
        }

        public update(data: IReferenceLine): void {
            this.data = data;

            // Update fields only if they differ to avoid cursor jumping or unnecessary reflows
            if (this.$label.val() !== (this.data.label || ''))
                this.$label.val(this.data.label || '');

            if (this.$type.val() !== (this.data.type || 'Constant'))
                this.$type.val(this.data.type || 'Constant').trigger('change.select2');

            if (this.$style.val() !== (this.data.lineStyle || 'dashed'))
                this.$style.val(this.data.lineStyle || 'dashed').trigger('change.select2');

            if (this.$value.val() !== (this.data.value?.toString() || ''))
                this.$value.val(this.data.value ?? '');

            (this.$color as any).minicolors('value', this.data.color || '#000000');

            if (this.data.type === 'Constant') {
                this.$value.show();
                this.$valuePlaceholder.hide();
            } else {
                this.$value.hide();
                this.$valuePlaceholder.show();
            }
        }

        private bindEvents(): void {
            this.$removeBtn.on('click', () => this.onDelete());
            this.$label.on('input', e => {
                this.data.label = $(e.target).val() as string;
                this.onChange(this.data);
            });
            this.$value.on('input', e => {
                const valStr = $(e.target).val() as string;
                this.data.value = valStr === '' ? undefined : parseFloat(valStr);
                this.onChange(this.data);
            });

            this.$type.on('change', e => {
                this.data.type = $(e.target).val() as any;
                // We call update() immediately to toggle the Value input visibility
                this.update(this.data);
                this.onChange(this.data);
            });

            this.$style.on('change', e => {
                this.data.lineStyle = $(e.target).val() as any;
                this.onChange(this.data);
            });

            this.$color.on('change', e => {
                this.data.color = $(e.target).val() as string;
                this.onChange(this.data);
            });
        }

        public destroy(): void {
            this.$row.remove();
        }
    }
    class ReferenceLinesEditor extends DataQueryEditorBase {
        private $list!: JQuery;
        private $addBtn!: JQuery;
        private rowEditors: ReferenceLineRowEditor[] = [];

        init() {
            this.$list = this.$editor.find('.ref-lines-list');
            this.$addBtn = this.$editor.find('.add-ref-line');
        }

        update() {
            const lines = this.getValue() as IReferenceLine[] || [];

            while (this.rowEditors.length > lines.length)
                this.rowEditors.pop()?.destroy();
            

            lines.forEach((line, index) => {
                if (this.rowEditors.length <= index) {
                    const onChange = (newData: IReferenceLine) => {
                        const current = this.getValueForEdit<IReferenceLine[]>() || [];
                        current[index] = newData;
                        this.setValue(current);
                    };

                    const onDelete = () => {
                        const current = this.getValueForEdit<IReferenceLine[]>() || [];
                        current.splice(index, 1);
                        this.setValue(current);
                    };

                    const editor = new ReferenceLineRowEditor(this.$list, line, onChange, onDelete);
                    this.rowEditors.push(editor);
                } else
                    this.rowEditors[index].update(line);
            });
        }

        bindEvents() {
            this.$addBtn.on('click', () => {
                const current = this.getValueForEdit<IReferenceLine[]>() || [];
                current.push({
                    type: 'Constant',
                    color: '#ff0000',
                    value: undefined,
                    label: 'Ref Line',
                    lineStyle: 'dashed'
                });
                this.setValue(current);
            });
        }

        destroy() {
            this.rowEditors.forEach(r => r.destroy());
            this.rowEditors = [];
            super.destroy();
        }
    }

    interface IArtifactsFolderItem {
        value: string;
        text: string;
        isArtifact: boolean;
    }

    class ScopeItemRowEditor {
        private $row: JQuery;
        private $itemSelect: JQuery;
        private $recursiveCheckbox: JQuery;
        private $deleteBtn: JQuery;
        private scopeItem: IArtifactScopeItem;
        private availableItems: IArtifactsFolderItem[];
        private usedIds: Set<string>;

        private onChanged: (newItem: IArtifactScopeItem) => void;
        private onDelete: () => void;

        constructor(
            container: JQuery,
            scopeItem: IArtifactScopeItem,
            availableItems: IArtifactsFolderItem[],
            usedIds: Set<string>,
            onChanged: (item: IArtifactScopeItem) => void,
            onDelete: () => void
        ) {
            this.$row = appn.utils.cloneTemplate('editor-scopeItemRow');
            container.append(this.$row);

            this.scopeItem = scopeItem;
            this.availableItems = availableItems;
            this.usedIds = usedIds;
            this.onChanged = onChanged;
            this.onDelete = onDelete;

            this.$itemSelect = this.$row.find('.scope-item-select');
            this.$recursiveCheckbox = this.$row.find('.scope-recursive-checkbox');
            this.$deleteBtn = this.$row.find('.remove-scope-item');

            this.update(scopeItem, availableItems, usedIds);
            this.bindEvents();
        }

        update(scopeItem: IArtifactScopeItem, availableItems: IArtifactsFolderItem[], usedIds: Set<string>): void {
            this.scopeItem = scopeItem;
            this.availableItems = availableItems;
            this.usedIds = usedIds;

            this.$itemSelect.val(scopeItem.id);

            const selectedItem = availableItems.find(i => i.value === scopeItem.id);
            const isFolder = selectedItem && !selectedItem.isArtifact;

            this.$recursiveCheckbox.prop('checked', scopeItem.recursive || false);
            this.$recursiveCheckbox.prop('disabled', !isFolder);

            if (!isFolder) {
                this.$recursiveCheckbox.closest('div').hide();
            } else {
                this.$recursiveCheckbox.closest('div').show();
            }

            this.populateItemSelect(availableItems, usedIds);
        }

        populateItemSelect(availableItems: IArtifactsFolderItem[], usedIds: Set<string>): void {
            this.$itemSelect.empty().append('<option value="">Choose...</option>');

            const folders = availableItems.filter(i => !i.isArtifact);
            const docs = availableItems.filter(i => i.isArtifact);

            if (folders.length > 0) {
                const $folderGroup = $('<optgroup label="Folders"></optgroup>');
                folders.forEach(item => {
                    if (item.value === this.scopeItem.id || !usedIds.has(item.value)) {
                        $folderGroup.append($('<option>').val(item.value).text(item.text));
                    }
                });
                this.$itemSelect.append($folderGroup);
            }

            if (docs.length > 0) {
                const $docGroup = $('<optgroup label="Artifacts"></optgroup>');
                docs.forEach(item => {
                    if (item.value === this.scopeItem.id || !usedIds.has(item.value)) {
                        $docGroup.append($('<option>').val(item.value).text(item.text));
                    }
                });
                this.$itemSelect.append($docGroup);
            }

            this.$itemSelect.val(this.scopeItem.id);
        }

        bindEvents(): void {
            this.$deleteBtn.on('click', () => this.onDelete());

            this.$itemSelect.on('change', e => {
                const newId = $(e.target).val() as string;
                const selectedItem = this.availableItems.find(i => i.value === newId);
                const isFolder = selectedItem && !selectedItem.isArtifact;

                this.onChanged({
                    ...this.scopeItem,
                    id: newId,
                    recursive: isFolder ? this.scopeItem.recursive : false
                });
            });

            this.$recursiveCheckbox.on('change', e => {
                this.onChanged({
                    ...this.scopeItem,
                    recursive: $(e.target).prop('checked')
                });
            });
        }

        destroy(): void {
            this.$row.remove();
        }
    }

    class ScopeItemsEditor extends EditorBase {
        protected $scopeItemsList!: JQuery;
        protected $addScopeItem!: JQuery;
        private scopeItemEditors: ScopeItemRowEditor[] = [];

        init() {
            this.$scopeItemsList = this.$editor.find('.scope-items-list');
            this.$addScopeItem = this.$editor.find('.add-scope-item');
        }

        update(): void {
            const scopeItems = this.getValue() as IArtifactScopeItem[] || [];
            const availableItems = designer.getItemList() as IArtifactsFolderItem[];
            const usedIds = new Set(scopeItems.map(si => si.id));

            this.$addScopeItem.toggle(availableItems.length > scopeItems.length);

            while (this.scopeItemEditors.length > scopeItems.length)
                this.scopeItemEditors.pop()?.destroy();

            scopeItems.forEach((scopeItem, index) => {
                if (this.scopeItemEditors.length <= index) {
                    const onChange = (newItem: IArtifactScopeItem) => {
                        const currentItems = this.getValueForEdit<IArtifactScopeItem[]>() || [];
                        currentItems[index] = newItem;
                        this.setValue(currentItems);
                    };
                    const onDelete = () => {
                        const currentItems = this.getValueForEdit<IArtifactScopeItem[]>() || [];
                        currentItems.splice(index, 1);
                        this.setValue(currentItems);
                    };
                    const editor = new ScopeItemRowEditor(this.$scopeItemsList, scopeItem, availableItems, usedIds, onChange, onDelete);
                    this.scopeItemEditors.push(editor);
                } else {
                    this.scopeItemEditors[index].update(scopeItem, availableItems, usedIds);
                }
            });

            appn.select.initForDesign({
                elements: this.$editor.find('select'),
                omitWidth: true,
                minimumResultsForSearch: MIN_RESULTS_FOR_SEARCH
            });
        }

        bindEvents(): void {
            this.$addScopeItem.on('click', () => {
                const availableItems = designer.getItemList() as IArtifactsFolderItem[];
                const currentItems = this.getValueForEdit<IArtifactScopeItem[]>() || [];
                const usedIds = new Set(currentItems.map(si => si.id));

                const available = availableItems.find(item => !usedIds.has(item.value));
                if (!available) return;

                const newItem: IArtifactScopeItem = {
                    id: available.value,
                    recursive: false
                };

                this.setValue([...currentItems, newItem]);
            });
        }

        destroy(): void {
            this.scopeItemEditors.forEach(e => e.destroy());
            this.scopeItemEditors = [];
            super.destroy();
        }
    }

    // Register dataset-related editors
    editors.register('datasetSelector', DatasetSelectorEditor);
    editors.register('filtersEditor', FiltersEditor);
    editors.register('projectionSelector', ProjectionSelectorEditor);
    editors.register('metricsEditor', MetricsEditor);
    editors.register('columnSelector', ColumnSelectorEditor);
    editors.register('breakDownByLimitEditor', BreakDownByLimitEditor);
    editors.register('sortOrderEditor', SortOrderEditor);
    editors.register('aggregationFiltersEditor', AggregationFiltersEditor);
    editors.register('referenceLinesEditor', ReferenceLinesEditor);
    editors.register('scopeItemsEditor', ScopeItemsEditor);
}
